import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import {
  Bus, MapPin, Users, TrendingUp, Plus, Edit2, Trash2, Save,
  X, ChevronDown, ChevronRight, RefreshCw, AlertTriangle,
  CheckCircle, XCircle, Clock, Navigation, User, UserCheck,
  LayoutDashboard, Route, StopCircle, Map, Calendar, ScanEye,
  Send, Lightbulb, Link2, Zap, Camera, Eye, Upload, Shield,
  IndianRupee, Printer, CreditCard, Receipt,
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { transportAPI } from '../services/api';

// ─── Helpers ──────────────────────────────────────────────────────
const today = () => new Date().toISOString().split('T')[0];
const fmt = (val, fallback = '—') => (val != null && val !== '') ? val : fallback;

function KpiCard({ label, value, sub, icon: Icon, color = 'blue', small }) {
  const colors = {
    blue:   'bg-blue-50   text-blue-700   border-blue-200',
    green:  'bg-green-50  text-green-700  border-green-200',
    amber:  'bg-amber-50  text-amber-700  border-amber-200',
    red:    'bg-red-50    text-red-700    border-red-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    slate:  'bg-slate-50  text-slate-700  border-slate-200',
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]} flex items-start gap-3`}>
      <div className="mt-0.5 shrink-0"><Icon size={small ? 16 : 20} /></div>
      <div>
        <p className="text-xs font-medium opacity-70">{label}</p>
        <p className={`font-bold ${small ? 'text-xl' : 'text-2xl'}`}>{value ?? '—'}</p>
        {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function Badge({ label, color = 'blue' }) {
  const c = {
    active:    'bg-green-100  text-green-800',
    inactive:  'bg-slate-100  text-slate-600',
    maintenance:'bg-amber-100 text-amber-800',
    scrapped:  'bg-red-100    text-red-700',
    present:   'bg-green-100  text-green-800',
    absent:    'bg-red-100    text-red-700',
    late:      'bg-amber-100  text-amber-800',
    boarded:   'bg-green-100  text-green-800',
    dropped:   'bg-blue-100   text-blue-800',
    missed_pickup:'bg-orange-100 text-orange-800',
    missed_drop:  'bg-red-100    text-red-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c[label] || 'bg-slate-100 text-slate-600'}`}>
      {label?.replace(/_/g,' ')}
    </span>
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`bg-white rounded-2xl shadow-2xl ${wide ? 'max-w-3xl' : 'max-w-lg'} w-full max-h-[90vh] overflow-y-auto`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="font-semibold text-slate-800 text-lg">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const selectCls = inputCls;

// ─── TABS ─────────────────────────────────────────────────────────
const TABS = [
  { id: 'dashboard',     label: 'Dashboard',    icon: LayoutDashboard },
  { id: 'vehicles',      label: 'Vehicles',     icon: Bus },
  { id: 'routes',        label: 'Routes',       icon: Route },
  { id: 'students',      label: 'Students',     icon: Users },
  { id: 'attendance',    label: 'Attendance',   icon: UserCheck },
  { id: 'gps',           label: 'GPS',          icon: Navigation },
  { id: 'anpr',          label: 'ANPR & Gates', icon: ScanEye },
  { id: 'analytics',     label: 'Analytics',    icon: TrendingUp },
  { id: 'exceptions',    label: 'Exceptions',   icon: AlertTriangle },
  { id: 'reports',       label: 'Reports',      icon: Map },
  { id: 'notifications', label: 'Alerts',       icon: CheckCircle },
  { id: 'optimization',  label: 'AI Optimize',  icon: Lightbulb },
  { id: 'recognition',   label: 'Recognition',  icon: Camera },
  { id: 'fees',          label: 'Fees',         icon: IndianRupee },
  { id: 'livecctv',     label: 'Live CCTV',    icon: Eye },
];

// ═══════════════════════════════════════════════════════════════════
// DASHBOARD TAB
// ═══════════════════════════════════════════════════════════════════
function DashboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await transportAPI.getDashboard();
      setData(r.data);
    } catch { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-20"><RefreshCw className="animate-spin text-blue-500" size={28} /></div>;
  if (!data)   return null;

  const { vehicles: v, students: s, drivers: d, helpers: h, routes: r, ai } = data;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-700">Today — {data.today}</h2>
        <button onClick={load} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Vehicles */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Vehicle Operations</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          <KpiCard label="Total Vehicles"   value={v.total}       icon={Bus}         color="slate" />
          <KpiCard label="Active"           value={v.active}      icon={CheckCircle} color="green" />
          <KpiCard label="Maintenance"      value={v.maintenance} icon={AlertTriangle} color="amber" />
          <KpiCard label="On Route"         value={v.on_route}    icon={Navigation}  color="blue" />
          <KpiCard label="Returned"         value={v.returned}    icon={CheckCircle} color="green" />
          <KpiCard label="Delayed"          value={v.late_arrival} icon={Clock}       color="amber" />
          <KpiCard label="Not Reported"     value={v.not_reported} icon={XCircle}    color="red" />
          <KpiCard label="Absent"           value={v.absent}      icon={XCircle}     color="red" />
        </div>
      </div>

      {/* Students */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Student Transport</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard label="Using Transport"  value={s.total_transport}    icon={Users}       color="blue" />
          <KpiCard label="Pickups Done"     value={s.pickups_completed}  icon={CheckCircle} color="green" sub={`${s.boarding_pct}%`} />
          <KpiCard label="Drops Done"       value={s.drops_completed}    icon={CheckCircle} color="green" sub={`${s.drop_pct}%`} />
          <KpiCard label="Missed Pickups"   value={s.missed_pickups}     icon={AlertTriangle} color="red" />
          <KpiCard label="Missed Drops"     value={s.missed_drops}       icon={AlertTriangle} color="red" />
          <KpiCard label="Routes Active"    value={r.total}              icon={Route}       color="purple" />
        </div>
      </div>

      {/* Staff */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Driver & Helper Attendance</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard label="Total Drivers"    value={d.total}   icon={User}        color="slate" />
          <KpiCard label="Drivers Present"  value={d.present} icon={UserCheck}   color="green" />
          <KpiCard label="Drivers Absent"   value={d.absent}  icon={XCircle}     color="red" />
          <KpiCard label="Total Helpers"    value={h.total}   icon={User}        color="slate" />
          <KpiCard label="Helpers Present"  value={h.present} icon={UserCheck}   color="green" />
          <KpiCard label="Helpers Absent"   value={h.absent}  icon={XCircle}     color="red" />
        </div>
      </div>

      {/* AI */}
      {(ai.route_suggestions > 0 || ai.open_exceptions > 0) && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="font-semibold text-amber-800 text-sm mb-2">AI Alerts</p>
          <div className="grid grid-cols-2 gap-3">
            <KpiCard label="Route Optimization Suggestions" value={ai.route_suggestions} icon={TrendingUp} color="amber" small />
            <KpiCard label="Open Exceptions"                value={ai.open_exceptions}   icon={AlertTriangle} color="red" small />
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// VEHICLES TAB
// ═══════════════════════════════════════════════════════════════════
const BLANK_VEHICLE = {
  vehicle_number:'', registration_number:'', vehicle_type:'bus', ownership_type:'school_owned',
  make:'', model:'', manufacturing_year:'', seating_capacity:'', status:'active',
  assigned_driver_id:'', assigned_helper_id:'', assigned_route_id:'',
  insurance_number:'', insurance_expiry:'', fitness_expiry:'', pollution_expiry:'',
  gps_device_id:'', cctv_device_id:'', anpr_camera_id:'', notes:'',
};

function VehiclesTab() {
  const [vehicles,   setVehicles]   = useState([]);
  const [employees,  setEmployees]  = useState([]);
  const [routes,     setRoutes]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modal,      setModal]      = useState(null); // null | 'add' | 'edit'
  const [form,       setForm]       = useState(BLANK_VEHICLE);
  const [saving,     setSaving]     = useState(false);
  const [deleting,   setDeleting]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [vr, er, rr] = await Promise.all([
        transportAPI.getVehicles(),
        transportAPI.getEmployees(),
        transportAPI.getRoutes(),
      ]);
      setVehicles(vr.data);
      setEmployees(er.data);
      setRoutes(rr.data);
    } catch { toast.error('Failed to load vehicles'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setForm(BLANK_VEHICLE); setModal('add'); };
  const openEdit = v  => { setForm({ ...v }); setModal('edit'); };
  const closeModal = () => setModal(null);

  const save = async () => {
    if (!form.vehicle_number || !form.vehicle_type) return toast.error('Vehicle number and type are required');
    setSaving(true);
    try {
      if (modal === 'add') await transportAPI.createVehicle(form);
      else                 await transportAPI.updateVehicle(form.id, form);
      toast.success(`Vehicle ${modal === 'add' ? 'added' : 'updated'}`);
      closeModal();
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const del = async id => {
    if (!window.confirm('Delete this vehicle?')) return;
    setDeleting(id);
    try {
      await transportAPI.deleteVehicle(id);
      toast.success('Vehicle deleted');
      load();
    } catch { toast.error('Delete failed'); }
    finally { setDeleting(null); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const expiring = (date) => {
    if (!date) return false;
    const d = new Date(date), now = new Date();
    const diff = (d - now) / (1000 * 60 * 60 * 24);
    return diff < 30;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-slate-700">{vehicles.length} vehicles</h2>
        <button onClick={openAdd} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
          <Plus size={15} /> Add Vehicle
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-blue-500" size={24} /></div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                {['Vehicle No','Type','Capacity','Driver','Helper','Route','Status','Insurance','Actions'].map(h => (
                  <th key={h} className="px-3 py-3 text-left font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {vehicles.map(v => (
                <tr key={v.id} className="hover:bg-slate-50">
                  <td className="px-3 py-3 font-semibold text-blue-700">{v.vehicle_number}</td>
                  <td className="px-3 py-3 capitalize">{v.vehicle_type?.replace('_',' ')}</td>
                  <td className="px-3 py-3 text-center">{fmt(v.seating_capacity)}</td>
                  <td className="px-3 py-3">{fmt(v.driver_name)}</td>
                  <td className="px-3 py-3">{fmt(v.helper_name)}</td>
                  <td className="px-3 py-3">{fmt(v.route_code)}</td>
                  <td className="px-3 py-3"><Badge label={v.status} /></td>
                  <td className="px-3 py-3">
                    {v.insurance_expiry
                      ? <span className={expiring(v.insurance_expiry) ? 'text-red-600 font-semibold' : 'text-slate-600'}>
                          {new Date(v.insurance_expiry).toLocaleDateString('en-IN')}
                          {expiring(v.insurance_expiry) && ' ⚠️'}
                        </span>
                      : '—'}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(v)} className="text-blue-600 hover:text-blue-800"><Edit2 size={14} /></button>
                      <button onClick={() => del(v.id)} disabled={deleting===v.id} className="text-red-500 hover:text-red-700">
                        {deleting===v.id ? <RefreshCw size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!vehicles.length && <p className="text-center py-12 text-slate-400">No vehicles found. Add your first vehicle.</p>}
        </div>
      )}

      {modal && (
        <Modal title={modal==='add' ? 'Add Vehicle' : 'Edit Vehicle'} onClose={closeModal} wide>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Vehicle Number" required>
              <input className={inputCls} value={form.vehicle_number} onChange={e=>set('vehicle_number',e.target.value)} placeholder="e.g. UP32AB1234" />
            </Field>
            <Field label="Registration No">
              <input className={inputCls} value={form.registration_number||''} onChange={e=>set('registration_number',e.target.value)} />
            </Field>
            <Field label="Vehicle Type" required>
              <select className={selectCls} value={form.vehicle_type} onChange={e=>set('vehicle_type',e.target.value)}>
                {['bus','cab','van','mini_bus'].map(t=><option key={t} value={t}>{t.replace('_',' ')}</option>)}
              </select>
            </Field>
            <Field label="Ownership">
              <select className={selectCls} value={form.ownership_type} onChange={e=>set('ownership_type',e.target.value)}>
                {['school_owned','vendor_owned','contracted'].map(t=><option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
              </select>
            </Field>
            <Field label="Make"><input className={inputCls} value={form.make||''} onChange={e=>set('make',e.target.value)} placeholder="e.g. Tata" /></Field>
            <Field label="Model"><input className={inputCls} value={form.model||''} onChange={e=>set('model',e.target.value)} placeholder="e.g. Starbus" /></Field>
            <Field label="Year"><input className={inputCls} type="number" value={form.manufacturing_year||''} onChange={e=>set('manufacturing_year',e.target.value)} /></Field>
            <Field label="Seating Capacity"><input className={inputCls} type="number" value={form.seating_capacity||''} onChange={e=>set('seating_capacity',e.target.value)} /></Field>
            <Field label="Status">
              <select className={selectCls} value={form.status} onChange={e=>set('status',e.target.value)}>
                {['active','inactive','maintenance','scrapped'].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Assigned Route">
              <select className={selectCls} value={form.assigned_route_id||''} onChange={e=>set('assigned_route_id',e.target.value)}>
                <option value="">— None —</option>
                {routes.map(r=><option key={r.id} value={r.id}>{r.route_code} – {r.route_name}</option>)}
              </select>
            </Field>
            <Field label="Driver">
              <select className={selectCls} value={form.assigned_driver_id||''} onChange={e=>set('assigned_driver_id',e.target.value)}>
                <option value="">— None —</option>
                {employees.map(e=><option key={e.id} value={e.id}>{e.name} ({e.employee_code})</option>)}
              </select>
            </Field>
            <Field label="Helper / Attendant">
              <select className={selectCls} value={form.assigned_helper_id||''} onChange={e=>set('assigned_helper_id',e.target.value)}>
                <option value="">— None —</option>
                {employees.map(e=><option key={e.id} value={e.id}>{e.name} ({e.employee_code})</option>)}
              </select>
            </Field>
            <Field label="Insurance Number"><input className={inputCls} value={form.insurance_number||''} onChange={e=>set('insurance_number',e.target.value)} /></Field>
            <Field label="Insurance Expiry"><input className={inputCls} type="date" value={form.insurance_expiry||''} onChange={e=>set('insurance_expiry',e.target.value)} /></Field>
            <Field label="Fitness Expiry"><input className={inputCls} type="date" value={form.fitness_expiry||''} onChange={e=>set('fitness_expiry',e.target.value)} /></Field>
            <Field label="Pollution Expiry"><input className={inputCls} type="date" value={form.pollution_expiry||''} onChange={e=>set('pollution_expiry',e.target.value)} /></Field>
            <Field label="GPS Device ID"><input className={inputCls} value={form.gps_device_id||''} onChange={e=>set('gps_device_id',e.target.value)} /></Field>
            <Field label="CCTV Device ID"><input className={inputCls} value={form.cctv_device_id||''} onChange={e=>set('cctv_device_id',e.target.value)} /></Field>
            <div className="col-span-2">
              <Field label="Notes"><textarea className={inputCls} rows={2} value={form.notes||''} onChange={e=>set('notes',e.target.value)} /></Field>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-5">
            <button onClick={closeModal} className="px-4 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50">Cancel</button>
            <button onClick={save} disabled={saving} className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : <><Save size={14} className="inline mr-1" />Save</>}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ROUTES & STOPS TAB
// ═══════════════════════════════════════════════════════════════════
const BLANK_ROUTE = { route_code:'', route_name:'', vehicle_id:'', driver_id:'', helper_id:'', total_distance:'', estimated_time:'', start_point:'', end_point:'', status:'active', notes:'' };
const BLANK_STOP  = { stop_name:'', route_id:'', sequence_number:'', pickup_time:'', drop_time:'', latitude:'', longitude:'', landmark:'', notes:'', status:'active' };

function RoutesTab() {
  const [routes,    setRoutes]    = useState([]);
  const [stops,     setStops]     = useState([]);
  const [vehicles,  setVehicles]  = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [expanded,  setExpanded]  = useState(null);
  const [modal,     setModal]     = useState(null); // {type:'route'|'stop', mode:'add'|'edit'}
  const [form,      setForm]      = useState({});
  const [saving,    setSaving]    = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rr, sr, vr, er] = await Promise.all([
        transportAPI.getRoutes(),
        transportAPI.getStops(),
        transportAPI.getVehicles(),
        transportAPI.getEmployees(),
      ]);
      setRoutes(rr.data);
      setStops(sr.data);
      setVehicles(vr.data);
      setEmployees(er.data);
    } catch { toast.error('Failed to load routes'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stopsFor = id => stops.filter(s => s.route_id === id).sort((a,b)=>a.sequence_number - b.sequence_number);

  const openRouteAdd  = ()  => { setForm(BLANK_ROUTE); setModal({ type:'route', mode:'add' }); };
  const openRouteEdit = r   => { setForm({ ...r }); setModal({ type:'route', mode:'edit' }); };
  const openStopAdd   = rid => { setForm({ ...BLANK_STOP, route_id: rid }); setModal({ type:'stop', mode:'add' }); };
  const openStopEdit  = s   => { setForm({ ...s }); setModal({ type:'stop', mode:'edit' }); };
  const closeModal = () => setModal(null);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const save = async () => {
    setSaving(true);
    try {
      if (modal.type === 'route') {
        if (!form.route_code || !form.route_name) return toast.error('Route code and name are required');
        if (modal.mode === 'add') await transportAPI.createRoute(form);
        else                      await transportAPI.updateRoute(form.id, form);
      } else {
        if (!form.stop_name || !form.route_id) return toast.error('Stop name and route are required');
        if (modal.mode === 'add') await transportAPI.createStop(form);
        else                      await transportAPI.updateStop(form.id, form);
      }
      toast.success('Saved successfully');
      closeModal();
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const delRoute = async id => {
    if (!window.confirm('Delete this route and all its stops?')) return;
    try { await transportAPI.deleteRoute(id); toast.success('Route deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };
  const delStop = async id => {
    if (!window.confirm('Delete this stop?')) return;
    try { await transportAPI.deleteStop(id); toast.success('Stop deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  if (loading) return <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-blue-500" size={24} /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-slate-700">{routes.length} routes</h2>
        <button onClick={openRouteAdd} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
          <Plus size={15} /> Add Route
        </button>
      </div>

      <div className="space-y-3">
        {routes.map(rt => (
          <div key={rt.id} className="border border-slate-200 rounded-xl overflow-hidden">
            <div
              className="flex items-center gap-3 p-4 bg-slate-50 cursor-pointer hover:bg-slate-100"
              onClick={() => setExpanded(expanded === rt.id ? null : rt.id)}
            >
              {expanded === rt.id ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
              <div className="flex-1 min-w-0">
                <span className="font-semibold text-blue-700 mr-2">{rt.route_code}</span>
                <span className="text-slate-700">{rt.route_name}</span>
                <span className="ml-3 text-xs text-slate-500">
                  {rt.stop_count || 0} stops · {rt.student_count || 0} students
                  {rt.vehicle_number && ` · ${rt.vehicle_number}`}
                  {rt.driver_name    && ` · ${rt.driver_name}`}
                </span>
              </div>
              <Badge label={rt.status} />
              <div className="flex gap-2 ml-2" onClick={e=>e.stopPropagation()}>
                <button onClick={()=>openRouteEdit(rt)} className="text-blue-600 hover:text-blue-800"><Edit2 size={14} /></button>
                <button onClick={()=>delRoute(rt.id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
              </div>
            </div>

            {expanded === rt.id && (
              <div className="p-4 border-t border-slate-100">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Stops</p>
                  <button onClick={() => openStopAdd(rt.id)} className="flex items-center gap-1 text-xs bg-green-600 text-white px-3 py-1 rounded-lg hover:bg-green-700">
                    <Plus size={11} /> Add Stop
                  </button>
                </div>
                {stopsFor(rt.id).length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No stops added yet</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-xs text-slate-500 bg-slate-50">
                      <tr>{['Seq','Stop Name','Pickup','Drop','Landmark','Students','Actions'].map(h=><th key={h} className="px-2 py-2 text-left">{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {stopsFor(rt.id).map(s => (
                        <tr key={s.id} className="hover:bg-slate-50">
                          <td className="px-2 py-2 font-mono text-center">{s.sequence_number}</td>
                          <td className="px-2 py-2 font-medium"><MapPin size={12} className="inline text-slate-400 mr-1" />{s.stop_name}</td>
                          <td className="px-2 py-2 text-slate-600">{s.pickup_time||'—'}</td>
                          <td className="px-2 py-2 text-slate-600">{s.drop_time||'—'}</td>
                          <td className="px-2 py-2 text-slate-500">{s.landmark||'—'}</td>
                          <td className="px-2 py-2 text-center">{(s.pickup_students||0)+(s.drop_students||0)}</td>
                          <td className="px-2 py-2">
                            <div className="flex gap-2">
                              <button onClick={()=>openStopEdit(s)} className="text-blue-600 hover:text-blue-800"><Edit2 size={13} /></button>
                              <button onClick={()=>delStop(s.id)} className="text-red-500 hover:text-red-700"><Trash2 size={13} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        ))}
        {!routes.length && <p className="text-center py-12 text-slate-400">No routes found. Create your first route.</p>}
      </div>

      {modal && (
        <Modal title={`${modal.mode==='add'?'Add':'Edit'} ${modal.type==='route'?'Route':'Stop'}`} onClose={closeModal} wide={modal.type==='route'}>
          {modal.type === 'route' ? (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Route Code" required><input className={inputCls} value={form.route_code||''} onChange={e=>set('route_code',e.target.value)} placeholder="e.g. R01" /></Field>
              <Field label="Route Name" required><input className={inputCls} value={form.route_name||''} onChange={e=>set('route_name',e.target.value)} /></Field>
              <Field label="Vehicle">
                <select className={selectCls} value={form.vehicle_id||''} onChange={e=>set('vehicle_id',e.target.value)}>
                  <option value="">— None —</option>
                  {vehicles.map(v=><option key={v.id} value={v.id}>{v.vehicle_number} ({v.vehicle_type})</option>)}
                </select>
              </Field>
              <Field label="Driver">
                <select className={selectCls} value={form.driver_id||''} onChange={e=>set('driver_id',e.target.value)}>
                  <option value="">— None —</option>
                  {employees.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </Field>
              <Field label="Helper">
                <select className={selectCls} value={form.helper_id||''} onChange={e=>set('helper_id',e.target.value)}>
                  <option value="">— None —</option>
                  {employees.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select className={selectCls} value={form.status} onChange={e=>set('status',e.target.value)}>
                  <option value="active">Active</option><option value="inactive">Inactive</option>
                </select>
              </Field>
              <Field label="Start Point"><input className={inputCls} value={form.start_point||''} onChange={e=>set('start_point',e.target.value)} /></Field>
              <Field label="End Point"><input className={inputCls} value={form.end_point||''} onChange={e=>set('end_point',e.target.value)} /></Field>
              <Field label="Total Distance (km)"><input className={inputCls} type="number" value={form.total_distance||''} onChange={e=>set('total_distance',e.target.value)} /></Field>
              <Field label="Estimated Time (min)"><input className={inputCls} type="number" value={form.estimated_time||''} onChange={e=>set('estimated_time',e.target.value)} /></Field>
              <div className="col-span-2"><Field label="Notes"><textarea className={inputCls} rows={2} value={form.notes||''} onChange={e=>set('notes',e.target.value)} /></Field></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Stop Name" required><input className={inputCls} value={form.stop_name||''} onChange={e=>set('stop_name',e.target.value)} /></Field>
              <Field label="Sequence No"><input className={inputCls} type="number" value={form.sequence_number||''} onChange={e=>set('sequence_number',e.target.value)} /></Field>
              <Field label="Pickup Time"><input className={inputCls} type="time" value={form.pickup_time||''} onChange={e=>set('pickup_time',e.target.value)} /></Field>
              <Field label="Drop Time"><input className={inputCls} type="time" value={form.drop_time||''} onChange={e=>set('drop_time',e.target.value)} /></Field>
              <Field label="Landmark"><input className={inputCls} value={form.landmark||''} onChange={e=>set('landmark',e.target.value)} /></Field>
              <Field label="Status">
                <select className={selectCls} value={form.status} onChange={e=>set('status',e.target.value)}>
                  <option value="active">Active</option><option value="inactive">Inactive</option>
                </select>
              </Field>
              <Field label="Latitude"><input className={inputCls} type="number" step="any" value={form.latitude||''} onChange={e=>set('latitude',e.target.value)} /></Field>
              <Field label="Longitude"><input className={inputCls} type="number" step="any" value={form.longitude||''} onChange={e=>set('longitude',e.target.value)} /></Field>
              <div className="col-span-2"><Field label="Notes"><textarea className={inputCls} rows={2} value={form.notes||''} onChange={e=>set('notes',e.target.value)} /></Field></div>
            </div>
          )}
          <div className="flex justify-end gap-3 mt-5">
            <button onClick={closeModal} className="px-4 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50">Cancel</button>
            <button onClick={save} disabled={saving} className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// STUDENT MAPPING TAB
// ═══════════════════════════════════════════════════════════════════
const BLANK_MAPPING = { student_id:'', pickup_stop_id:'', drop_stop_id:'', route_id:'', vehicle_id:'', transport_type:'bus', effective_date: today() };

function StudentsTab() {
  const [mappings,  setMappings]  = useState([]);
  const [students,  setStudents]  = useState([]);
  const [routes,    setRoutes]    = useState([]);
  const [stops,     setStops]     = useState([]);
  const [vehicles,  setVehicles]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modal,     setModal]     = useState(false);
  const [form,      setForm]      = useState(BLANK_MAPPING);
  const [saving,    setSaving]    = useState(false);
  const [search,    setSearch]    = useState('');
  const [portalModal, setPortalModal] = useState(null); // { student_id, student_name, url?, phone? }
  const [portalLoading, setPortalLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mr, sr, rr, stor, vr] = await Promise.all([
        transportAPI.getStudentMappings(),
        transportAPI.getStudents(),
        transportAPI.getRoutes(),
        transportAPI.getStops(),
        transportAPI.getVehicles(),
      ]);
      setMappings(mr.data);
      setStudents(sr.data);
      setRoutes(rr.data);
      setStops(stor.data);
      setVehicles(vr.data);
    } catch { toast.error('Failed to load student mappings'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const unmappedStudents = students.filter(s => !s.mapping_id);
  const stopsForRoute = rid => stops.filter(s => s.route_id === parseInt(rid));
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.student_id || !form.route_id) return toast.error('Student and route required');
    setSaving(true);
    try {
      if (form.id) await transportAPI.updateStudentMapping(form.id, form);
      else         await transportAPI.createStudentMapping(form);
      toast.success('Student mapping saved');
      setModal(false);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async id => {
    if (!window.confirm('Remove this student from transport?')) return;
    try { await transportAPI.deleteStudentMapping(id); toast.success('Removed'); load(); }
    catch { toast.error('Failed'); }
  };

  const openPortalModal = async (m) => {
    setPortalModal({ student_id: m.student_id || m.id, student_name: m.student_name, phone: m.father_phone });
    setPortalLoading(true);
    try {
      const res = await transportAPI.generateParentToken(m.student_id || m.id);
      setPortalModal(prev => ({ ...prev, ...res.data }));
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to generate link'); setPortalModal(null); }
    finally { setPortalLoading(false); }
  };

  const sendPortalSms = async () => {
    if (!portalModal?.student_id) return;
    setPortalLoading(true);
    try {
      await transportAPI.sendParentPortalSms(portalModal.student_id);
      toast.success('SMS sent to parent');
    } catch (e) { toast.error(e.response?.data?.error || 'SMS failed'); }
    finally { setPortalLoading(false); }
  };

  const filtered = mappings.filter(m =>
    !search || m.student_name?.toLowerCase().includes(search.toLowerCase()) ||
    m.admission_no?.toLowerCase().includes(search.toLowerCase()) ||
    m.class_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-1">
          <h2 className="font-semibold text-slate-700">{mappings.length} students on transport</h2>
          <input className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-48" placeholder="Search student…" value={search} onChange={e=>setSearch(e.target.value)} />
        </div>
        <button onClick={()=>{setForm(BLANK_MAPPING);setModal(true);}} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
          <Plus size={15} /> Map Student
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-blue-500" size={24} /></div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>{['Student','Class','Adm No','Route','Vehicle','Pickup Stop','Drop Stop','Type','Actions'].map(h=><th key={h} className="px-3 py-3 text-left">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(m => (
                <tr key={m.id} className="hover:bg-slate-50">
                  <td className="px-3 py-3 font-medium">{m.student_name}</td>
                  <td className="px-3 py-3 text-slate-600">{m.class_name} {m.section}</td>
                  <td className="px-3 py-3 text-slate-500">{m.admission_no}</td>
                  <td className="px-3 py-3 text-blue-700 font-medium">{m.route_code}</td>
                  <td className="px-3 py-3">{m.vehicle_number||'—'}</td>
                  <td className="px-3 py-3">{m.pickup_stop_name||'—'}<span className="text-xs text-slate-400 ml-1">{m.pickup_time||''}</span></td>
                  <td className="px-3 py-3">{m.drop_stop_name||'—'}</td>
                  <td className="px-3 py-3 capitalize">{m.transport_type}</td>
                  <td className="px-3 py-3">
                    <div className="flex gap-2">
                      <button onClick={()=>{setForm({...m});setModal(true);}} className="text-blue-600 hover:text-blue-800" title="Edit mapping"><Edit2 size={14} /></button>
                      <button onClick={()=>remove(m.id)} className="text-red-500 hover:text-red-700" title="Remove"><Trash2 size={14} /></button>
                      <button onClick={()=>openPortalModal(m)} className="text-green-600 hover:text-green-800" title="Parent Portal Link"><Link2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!filtered.length && <p className="text-center py-12 text-slate-400">No student mappings found.</p>}
        </div>
      )}

      {modal && (
        <Modal title={form.id ? 'Edit Student Mapping' : 'Map Student to Transport'} onClose={()=>setModal(false)} wide>
          <div className="grid grid-cols-2 gap-4">
            {!form.id && (
              <div className="col-span-2">
                <Field label="Student" required>
                  <select className={selectCls} value={form.student_id||''} onChange={e=>set('student_id',e.target.value)}>
                    <option value="">— Select student —</option>
                    {unmappedStudents.map(s=><option key={s.id} value={s.id}>{s.name} – {s.class_name} {s.section} ({s.admission_no})</option>)}
                  </select>
                </Field>
              </div>
            )}
            <Field label="Route" required>
              <select className={selectCls} value={form.route_id||''} onChange={e=>{set('route_id',e.target.value);set('pickup_stop_id','');set('drop_stop_id','');}}>
                <option value="">— Select route —</option>
                {routes.map(r=><option key={r.id} value={r.id}>{r.route_code} – {r.route_name}</option>)}
              </select>
            </Field>
            <Field label="Vehicle">
              <select className={selectCls} value={form.vehicle_id||''} onChange={e=>set('vehicle_id',e.target.value)}>
                <option value="">— Auto from route —</option>
                {vehicles.map(v=><option key={v.id} value={v.id}>{v.vehicle_number}</option>)}
              </select>
            </Field>
            <Field label="Pickup Stop">
              <select className={selectCls} value={form.pickup_stop_id||''} onChange={e=>set('pickup_stop_id',e.target.value)}>
                <option value="">— None —</option>
                {stopsForRoute(form.route_id).map(s=><option key={s.id} value={s.id}>{s.sequence_number}. {s.stop_name} {s.pickup_time?`(${s.pickup_time})`:''}</option>)}
              </select>
            </Field>
            <Field label="Drop Stop">
              <select className={selectCls} value={form.drop_stop_id||''} onChange={e=>set('drop_stop_id',e.target.value)}>
                <option value="">— None —</option>
                {stopsForRoute(form.route_id).map(s=><option key={s.id} value={s.id}>{s.sequence_number}. {s.stop_name}</option>)}
              </select>
            </Field>
            <Field label="Transport Type">
              <select className={selectCls} value={form.transport_type||'bus'} onChange={e=>set('transport_type',e.target.value)}>
                {['bus','cab','private'].map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Effective From">
              <input className={inputCls} type="date" value={form.effective_date||''} onChange={e=>set('effective_date',e.target.value)} />
            </Field>
          </div>
          <div className="flex justify-end gap-3 mt-5">
            <button onClick={()=>setModal(false)} className="px-4 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50">Cancel</button>
            <button onClick={save} disabled={saving} className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </Modal>
      )}

      {/* Parent Portal Link Modal */}
      {portalModal && (
        <Modal title={`Parent Portal — ${portalModal.student_name}`} onClose={()=>setPortalModal(null)}>
          {portalLoading && !portalModal.url ? (
            <div className="flex justify-center py-8"><RefreshCw className="animate-spin text-blue-500" size={24} /></div>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-xs text-slate-500 mb-1">Shareable link (no login required)</p>
                <div className="flex items-center gap-2">
                  <input readOnly value={portalModal.url||''} className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono bg-slate-50" />
                  <button onClick={()=>{navigator.clipboard.writeText(portalModal.url||'');toast.success('Copied!');}} className="px-3 py-2 text-xs bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 font-medium">Copy</button>
                </div>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-sm">
                <p className="text-blue-800 font-medium">{portalModal.parent||'Parent'}</p>
                <p className="text-blue-600 text-xs mt-0.5">{portalModal.phone||'No phone on record'}</p>
              </div>
              <button
                onClick={sendPortalSms}
                disabled={portalLoading || !portalModal.phone}
                className="w-full flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50"
              >
                <Send size={14} /> {portalLoading ? 'Sending…' : 'Send SMS to Parent'}
              </button>
              <p className="text-xs text-slate-400 text-center">This link is valid for 1 year. Parent sees live bus status, today's boarding/drop, and 7-day history.</p>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// CCTV SCAN PANEL (used inside AttendanceTab)
// ═══════════════════════════════════════════════════════════════════
function CCTVScanPanel({ date }) {
  const fileRef = useRef();
  const [preview,  setPreview]  = useState(null);
  const [image,    setImage]    = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result,   setResult]   = useState(null);

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = ev => { setPreview(ev.target.result); setImage(ev.target.result); setResult(null); };
    reader.readAsDataURL(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = ev => { setPreview(ev.target.result); setImage(ev.target.result); setResult(null); };
    reader.readAsDataURL(f);
  };

  const scan = async () => {
    if (!image) return;
    setScanning(true);
    setResult(null);
    try {
      const { data } = await transportAPI.cctvAttendance({ image_base64: image, date });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'CCTV scan failed');
    }
    setScanning(false);
  };

  const StatusBadge = ({ ok, label }) => ok
    ? <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full"><CheckCircle size={11}/>{label}</span>
    : <span className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">—</span>;

  return (
    <div className="space-y-4">
      {/* How it works */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { n: '1', title: 'Take Screenshot', desc: 'Capture frame from CCTV DVR / IP camera', color: 'bg-violet-50 border-violet-200 text-violet-700' },
          { n: '2', title: 'Plate Scan',       desc: 'AI reads number plate from the image',    color: 'bg-blue-50 border-blue-200 text-blue-700' },
          { n: '3', title: 'Face Scan',        desc: 'Driver / helper identified by face',      color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
          { n: '4', title: 'Auto Marked',      desc: 'Attendance saved with source = CCTV',     color: 'bg-amber-50 border-amber-200 text-amber-700' },
        ].map(s => (
          <div key={s.n} className={`rounded-xl border p-3 text-center ${s.color}`}>
            <div className="w-7 h-7 rounded-full bg-white bg-opacity-70 flex items-center justify-center text-xs font-bold mx-auto mb-1.5">{s.n}</div>
            <p className="text-xs font-semibold">{s.title}</p>
            <p className="text-xs opacity-70 mt-0.5">{s.desc}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Upload */}
        <div className="space-y-3">
          <div
            onDrop={handleDrop} onDragOver={e => e.preventDefault()}
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-slate-300 rounded-xl p-4 text-center cursor-pointer hover:border-violet-400 hover:bg-violet-50 transition-all min-h-52 flex flex-col items-center justify-center"
          >
            {preview
              ? <img src={preview} alt="CCTV frame" className="max-h-48 rounded-lg object-contain" />
              : <>
                  <Camera size={36} className="text-slate-300 mb-2" />
                  <p className="text-sm font-medium text-slate-500">Upload CCTV Frame</p>
                  <p className="text-xs text-slate-400 mt-1">Drag & drop or click — JPG / PNG</p>
                  <p className="text-xs text-slate-400">Best: gate camera with plate + driver visible</p>
                </>
            }
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />

          <div className="flex gap-2">
            <button onClick={() => { setPreview(null); setImage(null); setResult(null); }}
              className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
              Clear
            </button>
            <button onClick={scan} disabled={!image || scanning}
              className="flex-1 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-semibold hover:bg-violet-700 disabled:opacity-40 flex items-center justify-center gap-2">
              {scanning ? <RefreshCw size={14} className="animate-spin" /> : <Zap size={14} />}
              {scanning ? 'Scanning…' : 'Scan Frame'}
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-3">
          {/* Plate card */}
          <div className={`rounded-xl border p-4 transition-all ${result?.plate?.success ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50'}`}>
            <div className="flex items-center gap-2 mb-3">
              <ScanEye size={16} className={result?.plate?.success ? 'text-green-600' : 'text-slate-400'} />
              <span className="text-sm font-semibold text-slate-700">Number Plate</span>
              {result && <StatusBadge ok={result.plate?.success} label="Detected" />}
            </div>
            {!result
              ? <p className="text-xs text-slate-400">Awaiting scan…</p>
              : result.plate?.success
                ? <div className="space-y-2">
                    <p className="text-xl font-bold tracking-widest text-slate-800">{result.plate.text}</p>
                    <p className="text-xs text-slate-500">{Math.round((result.plate.confidence || 0) * 100)}% confidence</p>
                    {result.plate.vehicle
                      ? <div className="space-y-1">
                          <div className="flex items-center gap-1.5"><CheckCircle size={12} className="text-green-500" /><span className="text-xs text-green-700 font-medium">Vehicle: {result.plate.vehicle.number}</span></div>
                          {result.vehicle_marked && <div className="flex items-center gap-1.5"><CheckCircle size={12} className="text-green-500" /><span className="text-xs text-green-700">Vehicle attendance marked (route_started)</span></div>}
                          {result.plate.driver_auto_marked && <div className="flex items-center gap-1.5"><CheckCircle size={12} className="text-blue-500" /><span className="text-xs text-blue-700">Driver auto-marked: {result.plate.driver_auto_marked}</span></div>}
                          {result.plate.helper_auto_marked && <div className="flex items-center gap-1.5"><CheckCircle size={12} className="text-purple-500" /><span className="text-xs text-purple-700">Helper auto-marked: {result.plate.helper_auto_marked}</span></div>}
                        </div>
                      : <div className="flex items-center gap-1.5"><AlertTriangle size={12} className="text-amber-500" /><span className="text-xs text-amber-700">Plate read but no matching vehicle found</span></div>
                    }
                  </div>
                : <p className="text-xs text-slate-500">No number plate detected in this frame</p>
            }
          </div>

          {/* Face card */}
          <div className={`rounded-xl border p-4 transition-all ${result?.face?.name ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-slate-50'}`}>
            <div className="flex items-center gap-2 mb-3">
              <Eye size={16} className={result?.face?.name ? 'text-blue-600' : 'text-slate-400'} />
              <span className="text-sm font-semibold text-slate-700">Face Recognition</span>
              {result && <StatusBadge ok={!!result.face?.name} label="Identified" />}
            </div>
            {!result
              ? <p className="text-xs text-slate-400">Awaiting scan…</p>
              : result.face?.name
                ? <div className="space-y-2">
                    <p className="text-sm font-bold text-slate-800">{result.face.name}</p>
                    <p className="text-xs text-slate-500 capitalize">{result.face.category} • {Math.round((result.face.confidence || 0) * 100)}% match</p>
                    {(result.driver_marked || result.helper_marked) && (
                      <div className="flex items-center gap-1.5"><CheckCircle size={12} className="text-blue-500" /><span className="text-xs text-blue-700">Attendance marked as present</span></div>
                    )}
                  </div>
                : <p className="text-xs text-slate-500">{result.face?.success === false ? 'No face recognized — enroll face in Recognition tab first' : 'No face detected in this frame'}</p>
            }
          </div>

          {/* Errors */}
          {result?.errors?.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-1">
              {result.errors.map((e, i) => (
                <p key={i} className="text-xs text-amber-800"><span className="font-medium capitalize">{e.type}:</span> {e.message}</p>
              ))}
            </div>
          )}

          {/* Summary row */}
          {result && (
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Vehicle', ok: result.vehicle_marked, icon: Bus },
                { label: 'Driver',  ok: result.driver_marked,  icon: UserCheck },
                { label: 'Helper',  ok: result.helper_marked,  icon: Users },
              ].map(({ label, ok, icon: Icon }) => (
                <div key={label} className={`rounded-xl border p-2.5 text-center ${ok ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50'}`}>
                  <Icon size={16} className={`mx-auto mb-1 ${ok ? 'text-green-500' : 'text-slate-300'}`} />
                  <p className={`text-xs font-semibold ${ok ? 'text-green-700' : 'text-slate-400'}`}>{label}</p>
                  <p className={`text-xs ${ok ? 'text-green-600' : 'text-slate-400'}`}>{ok ? 'Marked ✓' : 'Not marked'}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tip */}
      <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 flex gap-2">
        <Lightbulb size={14} className="text-violet-500 mt-0.5 shrink-0" />
        <div>
          <p className="text-xs font-semibold text-violet-800">Tips for best results</p>
          <p className="text-xs text-violet-700 mt-0.5">• Use a frame where the number plate is clearly visible and well-lit · Plate should be ≥ 15% of frame width<br/>• For face recognition, ensure driver is facing the camera · Enroll faces via the <strong>Recognition</strong> tab first</p>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ATTENDANCE TAB
// ═══════════════════════════════════════════════════════════════════
// ── Shared Live CCTV Test Panel (used in Driver + Helper tabs) ──────
function CctvTestPanel({ open, cameras, selectedCam, watching, capturing, frame, result, marking, captureError, autoMarked, liveImgRef, onToggle, onSelectCam, onWatch, onStopWatch, onCapture, onMarkAttendance }) {
  const face        = result?.face;
  const plate       = result?.plate;
  const noDetection = result && !face && !plate;
  const liveUrl     = watching && selectedCam ? `/api/cctv-live/${selectedCam}` : null;

  return (
    <div className="rounded-xl border border-violet-200 bg-white overflow-hidden">
      {/* Header */}
      <button onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-violet-50 transition-colors">
        <div className="flex items-center gap-2">
          <Eye size={16} className="text-violet-600" />
          <span className="font-medium text-sm text-slate-700">Live CCTV Test</span>
          {watching && <span className="flex items-center gap-1 text-xs text-green-600 font-medium"><span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block"/>LIVE</span>}
          {!watching && <span className="text-xs text-slate-400">— view live feed &amp; test face detection</span>}
        </div>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="border-t border-violet-100 p-4 space-y-3">
          {/* Controls row */}
          <div className="flex gap-2 flex-wrap items-center">
            <select value={selectedCam} onChange={e => { onSelectCam(e.target.value); if (watching) onStopWatch(); }}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm flex-1 min-w-[180px]">
              <option value="">— Select Camera —</option>
              {cameras.map(c => (
                <option key={c.id} value={c.id}>{c.name}{c.gate_name ? ` · ${c.gate_name}` : ''}</option>
              ))}
            </select>

            {/* Watch Live toggle */}
            {!watching
              ? <button onClick={onWatch} disabled={!selectedCam}
                  className="flex items-center gap-1.5 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium disabled:opacity-40">
                  <Eye size={14}/> Watch Live
                </button>
              : <button onClick={onStopWatch}
                  className="flex items-center gap-1.5 px-4 py-2 bg-slate-200 text-slate-700 rounded-lg text-sm font-medium">
                  <XCircle size={14}/> Stop
                </button>
            }

            {/* While watching: show auto-detect pulse; otherwise manual Detect Now */}
            {watching
              ? <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2 text-xs text-violet-700 font-medium bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75"/>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-violet-600"/>
                    </span>
                    Scanning for faces…
                  </div>
                  <div className="flex items-center gap-2 text-xs text-blue-700 font-medium bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"/>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600"/>
                    </span>
                    Scanning for plates…
                  </div>
                </div>
              : frame && (
                <button onClick={onCapture} disabled={!selectedCam || capturing}
                  className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-40">
                  {capturing ? <><RefreshCw size={14} className="animate-spin"/>Scanning...</> : <><ScanEye size={14}/>Detect Again</>}
                </button>
              )
            }

            {cameras.length === 0 && (
              <p className="text-xs text-amber-600 w-full">No cameras configured. Go to Live CCTV tab → Cameras to add one.</p>
            )}
          </div>

          {/* Live feed */}
          {liveUrl && (
            <div className="space-y-1">
              <p className="text-xs text-slate-500 font-medium">Live Feed</p>
              <div className="rounded-xl overflow-hidden border border-slate-200 bg-black" style={{maxWidth: 640}}>
                <img
                  ref={liveImgRef}
                  src={liveUrl}
                  alt="Live CCTV"
                  crossOrigin="anonymous"
                  className="w-full h-auto block"
                  style={{maxHeight: 360, objectFit: 'contain'}}
                  onError={() => onStopWatch()}
                />
              </div>
              <p className="text-xs text-violet-600">Faces & number plates are scanned every 4 seconds. Attendance is marked automatically on first detection.</p>
            </div>
          )}

          {/* Snapshot + detection result */}
          {frame && (
            <div className="flex gap-4 flex-wrap items-start">
              <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-100 shrink-0" style={{width: 240}}>
                <img src={`data:image/jpeg;base64,${frame}`} alt="Detected frame" className="w-full h-auto block"/>
                <p className="text-center text-xs text-slate-400 py-1">Analysed frame</p>
              </div>
              <div className="flex-1 min-w-[200px] space-y-3">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Detection Result</p>

                {face && (
                  <div className="rounded-lg border border-green-200 bg-green-50 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-green-600"/>
                      <span className="font-semibold text-green-800">{face.name}</span>
                      <span className="text-xs bg-green-200 text-green-800 px-1.5 py-0.5 rounded capitalize">{face.category}</span>
                    </div>
                    <p className="text-xs text-green-700">Confidence: <strong>{Math.round((face.confidence||0)*100)}%</strong></p>
                    {marking && (
                      <div className="flex items-center gap-1.5 text-xs text-green-700">
                        <RefreshCw size={12} className="animate-spin"/> Marking present…
                      </div>
                    )}
                    {autoMarked && !marking && (
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-green-700">
                        <CheckCircle size={13}/> Auto-marked present
                      </div>
                    )}
                    {!autoMarked && !marking && (
                      <button onClick={() => onMarkAttendance(face)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold">
                        <CheckCircle size={12}/> Mark {face.name} Present
                      </button>
                    )}
                  </div>
                )}

                {plate && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <Bus size={16} className="text-blue-600"/>
                      <span className="font-bold text-blue-800 tracking-widest text-sm">
                        {plate.vehicle?.vehicle_number || plate.text}
                      </span>
                      <span className="text-xs bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded">Vehicle</span>
                    </div>
                    {plate.vehicle?.vehicle_number && plate.vehicle.vehicle_number !== plate.text && (
                      <p className="text-xs text-blue-500">OCR read: {plate.text} · {Math.round((plate.confidence||0)*100)}% confidence</p>
                    )}
                    {plate.autoMarked && (
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-700">
                        <CheckCircle size={13}/> Auto-marked route started
                      </div>
                    )}
                  </div>
                )}

                {noDetection && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={15} className="text-amber-500"/>
                      <span className="font-medium text-amber-700 text-sm">Nothing recognised</span>
                    </div>
                    <ul className="text-xs text-amber-700 list-disc list-inside space-y-0.5 mt-1">
                      <li>Driver's face not enrolled — Recognition tab → Enroll Face</li>
                      <li>Person not clearly visible / facing away from camera</li>
                      <li>Poor lighting — try again with better light</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {captureError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-1">
              <div className="flex items-center gap-2">
                <XCircle size={15} className="text-red-500"/>
                <span className="font-medium text-red-700 text-sm">Error: {captureError}</span>
              </div>
              <ul className="text-xs text-red-600 list-disc list-inside space-y-0.5 mt-1">
                <li>Check RTSP URL in Live CCTV → Cameras tab</li>
                <li>Camera must be on the same network as this server</li>
                <li>Recognition service must be running: <code className="bg-red-100 px-1 rounded">pm2 status</code></li>
              </ul>
            </div>
          )}

          {!watching && !frame && !captureError && (
            <p className="text-xs text-slate-400">Select a camera → Watch Live to see the feed → Detect Now to run face recognition on the current frame.</p>
          )}
        </div>
      )}
    </div>
  );
}

function AttendanceTab() {
  const [date,         setDate]         = useState(today());
  const [subTab,       setSubTab]       = useState('vehicle');
  const [vAttendance,  setVAttendance]  = useState([]);
  const [dAttendance,  setDAttendance]  = useState([]);
  const [hAttendance,  setHAttendance]  = useState([]);
  const [sAttendance,  setSAttendance]  = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [saving,       setSaving]       = useState({});
  const [cctvTest, setCctvTest] = useState({ open: false, cameras: [], selectedCam: '', watching: false, capturing: false, frame: null, result: null, marking: false, captureError: null, autoMarked: false });
  const liveImgRef       = useRef(null);
  const markedTodayRef      = useRef(new Set());
  const autoDetectCallback  = useRef(null);   // always holds latest detect fn, no stale closures

  const loadAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date };
      const [vr, dr, hr, sr] = await Promise.all([
        transportAPI.getVehicleAttendance(params),
        transportAPI.getDriverAttendance(params),
        transportAPI.getHelperAttendance(params),
        transportAPI.getStudentAttendance(params),
      ]);
      setVAttendance(vr.data.records);
      setDAttendance(dr.data.records);
      setHAttendance(hr.data.records);
      setSAttendance(sr.data.records);
    } catch { toast.error('Failed to load attendance'); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { loadAttendance(); }, [loadAttendance]);

  // Reset marked-today cache when date changes
  useEffect(() => { markedTodayRef.current.clear(); }, [date]);

  // Keep autoDetectCallback.current up-to-date on every render so the interval
  // always runs with the latest date / loadAttendance — no stale closures.
  useEffect(() => {
    autoDetectCallback.current = async () => {
      const img = liveImgRef.current;
      if (!img || !img.naturalWidth) return;   // feed not ready yet

      try {
        const canvas = document.createElement('canvas');
        canvas.width  = img.naturalWidth  || 640;
        canvas.height = img.naturalHeight || 360;
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        const frameB64 = canvas.toDataURL('image/jpeg', 0.8).split(',')[1];

        // Run face + plate recognition on the same frame simultaneously
        const [faceRes, plateRes] = await Promise.allSettled([
          transportAPI.recognizeFace({ image_base64: frameB64 }),
          transportAPI.recognizePlate({ image_base64: frameB64 }),
        ]);

        const face  = faceRes.status  === 'fulfilled' ? faceRes.value.data?.best_match : null;
        const plate = plateRes.status === 'fulfilled' ? plateRes.value.data            : null;

        const newResult = {};
        let faceAutoMarked = false;

        // ── Face → driver / helper attendance ────────────────────────
        if (face?.matched) {
          newResult.face = face;
          const fKey = `face:${face.person_id}`;
          faceAutoMarked = markedTodayRef.current.has(fKey);
          if (!faceAutoMarked) {
            const type = face.category === 'helper' ? 'helper' : 'driver';
            const fn   = type === 'driver' ? transportAPI.saveDriverAttendance : transportAPI.saveHelperAttendance;
            await fn({
              [`${type}_id`]:   face.person_id,
              vehicle_id:       null,
              attendance_date:  date,
              status:           'present',
              check_in_time:    new Date().toISOString().slice(0, 19).replace('T', ' '),
              detection_source: 'face_recognition',
            });
            markedTodayRef.current.add(fKey);
            toast.success(`${face.name} marked present`);
            loadAttendance();
            faceAutoMarked = true;
          }
        }

        // ── Plate → vehicle attendance ────────────────────────────────
        if (plate?.success && plate?.vehicle?.id) {
          const pKey = `plate:${plate.vehicle.id}`;
          const plateWasMarked = markedTodayRef.current.has(pKey);
          if (!plateWasMarked) {
            await transportAPI.saveVehicleAttendance({
              vehicle_id:       plate.vehicle.id,
              attendance_date:  date,
              status:           'route_started',
              departure_time:   new Date().toISOString().slice(0, 19).replace('T', ' '),
              detection_source: 'anpr',
            });
            markedTodayRef.current.add(pKey);
            toast.success(`Vehicle ${plate.vehicle.vehicle_number} marked route started`);
            loadAttendance();
          }
          newResult.plate = {
            text:        plate.plate_text,
            confidence:  plate.confidence,
            vehicle:     plate.vehicle,
            autoMarked:  true,
          };
        }

        if (face?.matched || (plate?.success && plate?.vehicle?.id)) {
          setCctvTest(t => ({ ...t, frame: frameB64, result: newResult, autoMarked: faceAutoMarked, captureError: null }));
        }
      } catch (e) {
        const msg = e?.response?.data?.error || e?.message || '';
        if (msg) setCctvTest(t => ({ ...t, captureError: msg }));
      }
    };
  });   // no dep array → runs every render, ref always fresh

  // Interval: fires when watching starts, stops when watching stops.
  // Calls via ref so it always gets the latest callback.
  useEffect(() => {
    if (!cctvTest.watching) return;
    const run = () => autoDetectCallback.current?.();
    run();                                       // immediate first check
    const id = setInterval(run, 4000);
    return () => clearInterval(id);
  }, [cctvTest.watching, cctvTest.selectedCam]);

  const saveVehicle = async (rec, field, value) => {
    const key = `v-${rec.vehicle_id}`;
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await transportAPI.saveVehicleAttendance({
        vehicle_id: rec.vehicle_id, attendance_date: date,
        status: field === 'status' ? value : rec.status || 'present',
        arrival_time: field === 'arrival_time' ? value : rec.arrival_time,
        departure_time: field === 'departure_time' ? value : rec.departure_time,
        return_time: field === 'return_time' ? value : rec.return_time,
        detection_source: 'manual',
      });
      loadAttendance();
    } catch { toast.error('Save failed'); }
    finally { setSaving(s => ({ ...s, [key]: false })); }
  };

  const savePersonAttendance = async (type, rec, status) => {
    const key = `${type}-${rec[type==='driver'?'driver_id':'helper_id']}`;
    setSaving(s => ({ ...s, [key]: true }));
    try {
      const fn = type === 'driver' ? transportAPI.saveDriverAttendance : transportAPI.saveHelperAttendance;
      await fn({ [`${type}_id`]: rec[`${type}_id`], vehicle_id: rec.vehicle_id, attendance_date: date, status, detection_source: 'manual' });
      loadAttendance();
    } catch { toast.error('Save failed'); }
    finally { setSaving(s => ({ ...s, [key]: false })); }
  };

  const saveStudentAttendance = async (rec, attendanceType, status) => {
    try {
      await transportAPI.saveStudentAttendance({
        student_id: rec.student_id, vehicle_id: rec.vehicle_id, route_id: rec.route_id,
        attendance_date: date, attendance_type: attendanceType, status,
        event_time: new Date().toISOString(), detection_source: 'manual',
      });
      loadAttendance();
    } catch { toast.error('Save failed'); }
  };

  const ATTEND_STATUS = {
    vehicle: ['not_reported','present','route_started','on_route','route_completed','returned','late_arrival','absent','maintenance'],
    person:  ['present','late','absent','half_day'],
    boarding:['boarded','absent','missed_pickup'],
    drop:    ['dropped','absent','missed_drop'],
  };

  const openCctvTest = async () => {
    if (!cctvTest.open) {
      try {
        const r = await transportAPI.getCCTVCameras();
        setCctvTest(t => ({ ...t, cameras: r.data || [], open: true, frame: null, result: null }));
      } catch { setCctvTest(t => ({ ...t, open: true })); }
    } else {
      setCctvTest(t => ({ ...t, open: false }));
    }
  };

  const runCCTVCapture = async () => {
    if (!cctvTest.selectedCam) return toast.error('Select a camera first');
    setCctvTest(t => ({ ...t, capturing: true, frame: null, result: null, captureError: null }));

    // ── Path A: live stream is open → grab visible frame from <img> via canvas
    // (no second RTSP connection; most cameras only allow one at a time)
    if (cctvTest.watching && liveImgRef.current) {
      try {
        const img    = liveImgRef.current;
        const canvas = document.createElement('canvas');
        canvas.width  = img.naturalWidth  || 640;
        canvas.height = img.naturalHeight || 360;
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        const frameB64 = canvas.toDataURL('image/jpeg', 0.85).split(',')[1];

        const r    = await transportAPI.recognizeFace({ image_base64: frameB64 });
        const best = r.data?.best_match;
        const matchedFace = best?.matched ? best : null;
        setCctvTest(t => ({
          ...t, capturing: false, frame: frameB64,
          result: { face: matchedFace },
          captureError: null, autoMarked: false,
        }));
        if (matchedFace) await markCCTVAttendance(matchedFace, true);
      } catch (e) {
        setCctvTest(t => ({ ...t, capturing: false, captureError: e.response?.data?.error || 'Recognition failed' }));
      }
      return;
    }

    // ── Path B: no live stream → open RTSP snapshot (models pre-warmed)
    try {
      const r = await transportAPI.captureCCTVSnapshot(cctvTest.selectedCam);
      const snapResult = r.data.result || {};
      const matchedFace = snapResult.face?.matched ? snapResult.face : null;
      setCctvTest(t => ({ ...t, capturing: false, frame: r.data.frame_base64, result: snapResult, captureError: null, autoMarked: false }));
      if (matchedFace) await markCCTVAttendance(matchedFace, true);
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Capture failed';
      setCctvTest(t => ({ ...t, capturing: false, captureError: msg }));
    }
  };

  const startLiveWatch = () => {
    if (!cctvTest.selectedCam) return toast.error('Select a camera first');
    setCctvTest(t => ({ ...t, watching: true, frame: null, result: null, captureError: null }));
  };

  const stopLiveWatch = () => setCctvTest(t => ({ ...t, watching: false }));

  const markCCTVAttendance = async (match, autoMark = false) => {
    if (!match?.person_id) return;
    setCctvTest(t => ({ ...t, marking: true }));
    try {
      const type = match.category === 'helper' ? 'helper' : 'driver';
      const fn = type === 'driver' ? transportAPI.saveDriverAttendance : transportAPI.saveHelperAttendance;
      await fn({
        [`${type}_id`]: match.person_id,
        vehicle_id: null,
        attendance_date: date,
        status: 'present',
        check_in_time: new Date().toISOString().slice(0, 19).replace('T', ' '),
        detection_source: 'face_recognition',
      });
      toast.success(`${match.name} marked present`);
      loadAttendance();
      if (autoMark) {
        setCctvTest(t => ({ ...t, marking: false, autoMarked: true }));
      } else {
        setCctvTest(t => ({ ...t, marking: false, open: false, frame: null, result: null, autoMarked: false }));
      }
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to mark attendance';
      toast.error(msg);
      setCctvTest(t => ({ ...t, marking: false }));
    }
  };

  const SrcBadge = ({ src }) => {
    if (!src || src === 'manual') return null;
    const map = {
      cctv:             { label: 'CCTV',      cls: 'bg-purple-100 text-purple-700' },
      anpr:             { label: 'ANPR',       cls: 'bg-blue-100   text-blue-700'   },
      face_recognition: { label: 'Face',       cls: 'bg-indigo-100 text-indigo-700' },
      gps:              { label: 'GPS',        cls: 'bg-teal-100   text-teal-700'   },
      biometric:        { label: 'Biometric',  cls: 'bg-amber-100  text-amber-700'  },
    };
    const { label, cls } = map[src] || { label: src, cls: 'bg-slate-100 text-slate-600' };
    return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${cls}`}>{label}</span>;
  };

  const statusColor = s => ({
    present:'text-green-700', route_started:'text-blue-700', on_route:'text-blue-600',
    route_completed:'text-green-700', returned:'text-green-600', late_arrival:'text-amber-600',
    absent:'text-red-600', not_reported:'text-slate-400', maintenance:'text-amber-700',
    late:'text-amber-600', boarded:'text-green-700', dropped:'text-blue-700',
    missed_pickup:'text-orange-600', missed_drop:'text-red-600', half_day:'text-amber-600',
  }[s] || 'text-slate-600');

  const SUB_TABS = [
    { id:'vehicle',  label:'Vehicle'         },
    { id:'driver',   label:'Driver'          },
    { id:'helper',   label:'Helper'          },
    { id:'student',  label:'Student Boarding'},
    { id:'cctv',     label:'CCTV Scan'       },
  ];

  return (
    <div>
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-slate-500" />
          <input type="date" className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" value={date} onChange={e=>setDate(e.target.value)} />
        </div>
        <button onClick={loadAttendance} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Sub tabs */}
      <div className="flex gap-1 mb-4 bg-slate-100 p-1 rounded-lg w-fit">
        {SUB_TABS.map(t => (
          <button key={t.id} onClick={()=>setSubTab(t.id)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${subTab===t.id ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'cctv' ? <CCTVScanPanel date={date} /> : loading ? (
        <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-blue-500" size={24} /></div>
      ) : (
        <>
          {/* Vehicle Attendance */}
          {subTab === 'vehicle' && (
            <div className="space-y-4">
            {/* ── Live CCTV Test Panel (ANPR plate → vehicle attendance) ── */}
            <CctvTestPanel
              open={cctvTest.open}
              cameras={cctvTest.cameras}
              selectedCam={cctvTest.selectedCam}
              watching={cctvTest.watching}
              capturing={cctvTest.capturing}
              frame={cctvTest.frame}
              result={cctvTest.result}
              marking={cctvTest.marking}
              captureError={cctvTest.captureError}
              autoMarked={cctvTest.autoMarked}
              liveImgRef={liveImgRef}
              onToggle={openCctvTest}
              onSelectCam={id => setCctvTest(t => ({ ...t, selectedCam: id }))}
              onWatch={startLiveWatch}
              onStopWatch={stopLiveWatch}
              onCapture={runCCTVCapture}
              onMarkAttendance={markCCTVAttendance}
            />

            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-600 uppercase">
                  <tr>{['Vehicle','Type','Route','Driver','Helper','Status','Arrival','Departure','Return'].map(h=><th key={h} className="px-3 py-3 text-left">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {vAttendance.map(rec => (
                    <tr key={rec.vehicle_id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-semibold text-blue-700">{rec.vehicle_number}</td>
                      <td className="px-3 py-2 capitalize">{rec.vehicle_type}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{rec.route_name||'—'}</td>
                      <td className="px-3 py-2 text-slate-600">{rec.driver_name||'—'}</td>
                      <td className="px-3 py-2 text-slate-600">{rec.helper_name||'—'}</td>
                      <td className="px-3 py-2">
                        <select className={`border rounded px-2 py-1 text-xs ${statusColor(rec.status)}`}
                          value={rec.status||'not_reported'}
                          onChange={e=>saveVehicle(rec,'status',e.target.value)}>
                          {ATTEND_STATUS.vehicle.map(s=><option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
                        </select>
                        <div className="mt-1"><SrcBadge src={rec.detection_source} /></div>
                      </td>
                      <td className="px-3 py-2">
                        <input type="time" className="border rounded px-2 py-1 text-xs w-24"
                          value={rec.arrival_time ? rec.arrival_time.slice(11,16) : ''}
                          onChange={e=>saveVehicle(rec,'arrival_time',e.target.value ? `${date}T${e.target.value}:00` : null)} />
                      </td>
                      <td className="px-3 py-2">
                        <input type="time" className="border rounded px-2 py-1 text-xs w-24"
                          value={rec.departure_time ? rec.departure_time.slice(11,16) : ''}
                          onChange={e=>saveVehicle(rec,'departure_time',e.target.value ? `${date}T${e.target.value}:00` : null)} />
                      </td>
                      <td className="px-3 py-2">
                        <input type="time" className="border rounded px-2 py-1 text-xs w-24"
                          value={rec.return_time ? rec.return_time.slice(11,16) : ''}
                          onChange={e=>saveVehicle(rec,'return_time',e.target.value ? `${date}T${e.target.value}:00` : null)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!vAttendance.length && <p className="text-center py-12 text-slate-400">No active vehicles found.</p>}
            </div>
            </div>
          )}

          {/* Driver Attendance */}
          {subTab === 'driver' && (
            <div className="space-y-4">
            {/* ── Live CCTV Test Panel ── */}
            <CctvTestPanel
              open={cctvTest.open}
              cameras={cctvTest.cameras}
              selectedCam={cctvTest.selectedCam}
              watching={cctvTest.watching}
              capturing={cctvTest.capturing}
              frame={cctvTest.frame}
              result={cctvTest.result}
              marking={cctvTest.marking}
              captureError={cctvTest.captureError}
              autoMarked={cctvTest.autoMarked}
              liveImgRef={liveImgRef}
              onToggle={openCctvTest}
              onSelectCam={id => setCctvTest(t => ({ ...t, selectedCam: id }))}
              onWatch={startLiveWatch}
              onStopWatch={stopLiveWatch}
              onCapture={runCCTVCapture}
              onMarkAttendance={markCCTVAttendance}
            />

            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-600 uppercase">
                  <tr>{['Driver','Code','Phone','Vehicle','Status','Mark Attendance'].map(h=><th key={h} className="px-3 py-3 text-left">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {dAttendance.map(rec => (
                    <tr key={rec.driver_id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium">{rec.driver_name}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{rec.employee_code}</td>
                      <td className="px-3 py-2 text-slate-500">{rec.phone||'—'}</td>
                      <td className="px-3 py-2">
                        {rec.vehicle_number
                          ? <span className="text-blue-700 font-medium">{rec.vehicle_number}</span>
                          : <span className="text-slate-400 text-xs italic">Not assigned</span>}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`font-medium ${statusColor(rec.status||'absent')}`}>{(rec.status||'not marked').replace(/_/g,' ')}</span>
                          <SrcBadge src={rec.detection_source} />
                        </div>
                        {rec.check_in_time && <p className="text-xs text-slate-400 mt-0.5">{new Date(rec.check_in_time).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}</p>}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1 flex-wrap">
                          {ATTEND_STATUS.person.map(s => (
                            <button key={s} onClick={()=>savePersonAttendance('driver',rec,s)}
                              className={`px-2 py-1 rounded text-xs border transition-all ${rec.status===s?'bg-blue-600 text-white border-blue-600':'text-slate-600 border-slate-300 hover:border-blue-400'}`}>
                              {s.replace(/_/g,' ')}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!dAttendance.length && <p className="text-center py-12 text-slate-400">No drivers found. Add employees with designation containing "Driver".</p>}
            </div>
            </div>
          )}

          {/* Helper Attendance */}
          {subTab === 'helper' && (
            <div className="space-y-4">
            {/* ── Live CCTV Test Panel ── */}
            <CctvTestPanel
              open={cctvTest.open}
              cameras={cctvTest.cameras}
              selectedCam={cctvTest.selectedCam}
              watching={cctvTest.watching}
              capturing={cctvTest.capturing}
              frame={cctvTest.frame}
              result={cctvTest.result}
              marking={cctvTest.marking}
              captureError={cctvTest.captureError}
              autoMarked={cctvTest.autoMarked}
              liveImgRef={liveImgRef}
              onToggle={openCctvTest}
              onSelectCam={id => setCctvTest(t => ({ ...t, selectedCam: id }))}
              onWatch={startLiveWatch}
              onStopWatch={stopLiveWatch}
              onCapture={runCCTVCapture}
              onMarkAttendance={markCCTVAttendance}
            />
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-600 uppercase">
                  <tr>{['Helper / Attendant','Code','Phone','Vehicle','Status','Mark Attendance'].map(h=><th key={h} className="px-3 py-3 text-left">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {hAttendance.map(rec => (
                    <tr key={rec.helper_id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium">{rec.helper_name}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{rec.employee_code}</td>
                      <td className="px-3 py-2 text-slate-500">{rec.phone||'—'}</td>
                      <td className="px-3 py-2">
                        {rec.vehicle_number
                          ? <span className="text-blue-700 font-medium">{rec.vehicle_number}</span>
                          : <span className="text-slate-400 text-xs italic">Not assigned</span>}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`font-medium ${statusColor(rec.status||'absent')}`}>{(rec.status||'not marked').replace(/_/g,' ')}</span>
                          <SrcBadge src={rec.detection_source} />
                        </div>
                        {rec.check_in_time && <p className="text-xs text-slate-400 mt-0.5">{new Date(rec.check_in_time).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}</p>}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1 flex-wrap">
                          {ATTEND_STATUS.person.map(s => (
                            <button key={s} onClick={()=>savePersonAttendance('helper',rec,s)}
                              className={`px-2 py-1 rounded text-xs border transition-all ${rec.status===s?'bg-blue-600 text-white border-blue-600':'text-slate-600 border-slate-300 hover:border-blue-400'}`}>
                              {s.replace(/_/g,' ')}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!hAttendance.length && <p className="text-center py-12 text-slate-400">No helpers found. Add employees with designation containing "Helper" or "Attendant".</p>}
            </div>
            </div>
          )}

          {/* Student Boarding / Drop */}
          {subTab === 'student' && (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-600 uppercase">
                  <tr>{['Student','Class','Adm No','Route','Vehicle','Pickup Stop','Boarding','Drop Stop','Drop'].map(h=><th key={h} className="px-3 py-3 text-left">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sAttendance.map(rec => (
                    <tr key={rec.student_id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium">{rec.student_name}</td>
                      <td className="px-3 py-2 text-slate-500">{rec.class_name} {rec.section}</td>
                      <td className="px-3 py-2 text-slate-400 text-xs">{rec.admission_no}</td>
                      <td className="px-3 py-2 text-blue-700 font-medium text-xs">{rec.route_name}</td>
                      <td className="px-3 py-2 text-xs">{rec.vehicle_number||'—'}</td>
                      <td className="px-3 py-2 text-xs">{rec.pickup_stop||'—'}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          {ATTEND_STATUS.boarding.map(s => (
                            <button key={s} onClick={()=>saveStudentAttendance(rec,'boarding',s)}
                              className={`px-2 py-0.5 rounded text-xs border transition-all ${rec.boarding_status===s?'bg-green-600 text-white border-green-600':'text-slate-500 border-slate-200 hover:border-green-400'}`}>
                              {s.replace(/_/g,' ')}
                            </button>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs">{rec.drop_stop||'—'}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          {ATTEND_STATUS.drop.map(s => (
                            <button key={s} onClick={()=>saveStudentAttendance(rec,'drop',s)}
                              className={`px-2 py-0.5 rounded text-xs border transition-all ${rec.drop_status===s?'bg-blue-600 text-white border-blue-600':'text-slate-500 border-slate-200 hover:border-blue-400'}`}>
                              {s.replace(/_/g,' ')}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!sAttendance.length && <p className="text-center py-12 text-slate-400">No students mapped to transport for this date.</p>}
            </div>
          )}

        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// GPS TRACKING TAB
// ═══════════════════════════════════════════════════════════════════
const SCHOOL_LAT = 26.9124;
const SCHOOL_LNG = 75.7873;

function GpsTrackingTab() {
  const mapRef         = useRef(null);
  const leafletMapRef  = useRef(null);
  const markersRef     = useRef({});
  const [vehicles,     setVehicles]     = useState([]);
  const [selected,     setSelected]     = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [lastRefresh,  setLastRefresh]  = useState(null);
  const [manualModal,  setManualModal]  = useState(false);
  const [form,         setForm]         = useState({ vehicle_id: '', latitude: '', longitude: '', speed: '' });
  const [saving,       setSaving]       = useState(false);

  const initMap = useCallback(() => {
    const L = window.L;
    if (!L || !mapRef.current || leafletMapRef.current) return;
    leafletMapRef.current = L.map(mapRef.current).setView([SCHOOL_LAT, SCHOOL_LNG], 12);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors', maxZoom: 19,
    }).addTo(leafletMapRef.current);
    L.marker([SCHOOL_LAT, SCHOOL_LNG], {
      icon: L.divIcon({ className: '', iconSize: [30, 30], iconAnchor: [15, 15],
        html: '<div style="background:#1e40af;color:#fff;width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)">TKS</div>' }),
    }).addTo(leafletMapRef.current).bindPopup('<b>The Kulish School</b>');
  }, []);

  const loadLeaflet = useCallback(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css'; link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    if (window.L) { initMap(); return; }
    if (document.getElementById('leaflet-js')) return;
    const script = document.createElement('script');
    script.id = 'leaflet-js';
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = initMap;
    document.head.appendChild(script);
  }, [initMap]);

  const fetchVehicles = useCallback(async () => {
    try {
      const res = await transportAPI.getGpsLive();
      setVehicles(res.data);
      setLastRefresh(new Date());
    } catch { toast.error('Failed to load GPS data'); }
    finally { setLoading(false); }
  }, []);

  const updateMarkers = useCallback(() => {
    const L = window.L;
    const map = leafletMapRef.current;
    if (!L || !map) return;
    const currentIds = new Set(vehicles.filter(v => v.latitude).map(v => String(v.vehicle_id)));
    Object.keys(markersRef.current).forEach(id => {
      if (!currentIds.has(id)) { map.removeLayer(markersRef.current[id]); delete markersRef.current[id]; }
    });
    vehicles.forEach(v => {
      if (!v.latitude || !v.longitude) return;
      const lat = parseFloat(v.latitude), lng = parseFloat(v.longitude);
      const stale = v.minutes_ago > 10;
      const bg = stale ? '#94a3b8' : (v.attendance_status === 'route_completed' ? '#16a34a' : '#2563eb');
      const html = `<div style="background:${bg};color:#fff;padding:2px 7px;border-radius:5px;font-size:10px;font-weight:700;white-space:nowrap;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3)">${v.vehicle_number}</div>`;
      const popup = `<div style="min-width:170px">
        <b style="color:#1e40af">${v.vehicle_number}</b>
        <span style="color:#64748b;font-size:11px"> ${v.vehicle_type || ''}</span><hr style="margin:4px 0">
        Route: <b>${v.route_name || '—'}</b><br>
        Driver: ${v.driver_name || '—'}<br>
        Speed: ${v.speed ? parseFloat(v.speed).toFixed(0) + ' km/h' : '—'}<br>
        Status: ${(v.attendance_status || '—').replace(/_/g, ' ')}<br>
        <span style="color:#94a3b8;font-size:11px">Updated: ${v.minutes_ago !== null ? v.minutes_ago + 'm ago' : '—'}</span>
      </div>`;
      const icon = L.divIcon({ className: '', html, iconAnchor: [0, 0] });
      const sid = String(v.vehicle_id);
      if (markersRef.current[sid]) {
        markersRef.current[sid].setLatLng([lat, lng]).setPopupContent(popup);
      } else {
        markersRef.current[sid] = L.marker([lat, lng], { icon }).addTo(map).bindPopup(popup);
      }
    });
  }, [vehicles]);

  useEffect(() => { loadLeaflet(); fetchVehicles(); }, []);
  useEffect(() => { if (window.L && leafletMapRef.current) updateMarkers(); }, [vehicles, updateMarkers]);
  useEffect(() => { const t = setInterval(fetchVehicles, 30000); return () => clearInterval(t); }, [fetchVehicles]);
  useEffect(() => () => { if (leafletMapRef.current) { leafletMapRef.current.remove(); leafletMapRef.current = null; } }, []);

  const saveManual = async () => {
    if (!form.vehicle_id || !form.latitude || !form.longitude) return toast.error('Vehicle, Latitude and Longitude are required');
    setSaving(true);
    try {
      await transportAPI.manualGpsEntry({ vehicle_id: form.vehicle_id, latitude: parseFloat(form.latitude), longitude: parseFloat(form.longitude), speed: form.speed ? parseFloat(form.speed) : null });
      toast.success('Location updated');
      setManualModal(false);
      setForm({ vehicle_id: '', latitude: '', longitude: '', speed: '' });
      fetchVehicles();
    } catch { toast.error('Failed to save location'); }
    finally { setSaving(false); }
  };

  const withGps = vehicles.filter(v => v.latitude);
  const noGps   = vehicles.filter(v => !v.latitude);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="text-sm text-slate-500">
          <span className="font-semibold text-slate-700">{withGps.length}</span> vehicles tracked
          {noGps.length > 0 && <span className="ml-2 text-amber-600">· {noGps.length} no GPS signal</span>}
        </div>
        {lastRefresh && <span className="text-xs text-slate-400">Last updated: {lastRefresh.toLocaleTimeString()}</span>}
        <div className="ml-auto flex gap-2">
          <button onClick={() => setManualModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50">
            <MapPin size={14} /> Manual Entry
          </button>
          <button onClick={fetchVehicles} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Vehicle list */}
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {loading && <div className="flex justify-center py-8"><RefreshCw className="animate-spin text-blue-500" size={20} /></div>}
          {vehicles.map(v => (
            <div key={v.vehicle_id} onClick={() => {
              setSelected(v);
              if (v.latitude && leafletMapRef.current) {
                leafletMapRef.current.setView([parseFloat(v.latitude), parseFloat(v.longitude)], 15);
                const m = markersRef.current[String(v.vehicle_id)];
                if (m) m.openPopup();
              }
            }} className={`p-3 rounded-xl border cursor-pointer transition-all hover:border-blue-400 ${selected?.vehicle_id === v.vehicle_id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm text-blue-700">{v.vehicle_number}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${v.latitude ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'}`}>
                  {v.latitude ? '● Live' : '○ No GPS'}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{v.route_name || 'No route'}</p>
              {v.driver_name && <p className="text-xs text-slate-400">{v.driver_name}</p>}
              {v.latitude && <p className="text-xs text-slate-400">{v.speed ? parseFloat(v.speed).toFixed(0) + ' km/h' : '—'}{v.minutes_ago !== null ? ` · ${v.minutes_ago}m ago` : ''}</p>}
            </div>
          ))}
          {!loading && !vehicles.length && <p className="text-sm text-slate-400 text-center py-8">No active vehicles found.</p>}
        </div>

        {/* Leaflet map */}
        <div className="lg:col-span-3 rounded-xl border border-slate-200 overflow-hidden" style={{ height: 500 }}>
          <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
        </div>
      </div>

      <div className="flex gap-5 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded bg-blue-600 inline-block" /> Active / En route</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded bg-green-600 inline-block" /> Route completed</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded bg-slate-400 inline-block" /> Stale (&gt;10 min)</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded bg-blue-900 inline-block" /> TKS School</span>
        <span className="ml-auto text-slate-400">Auto-refreshes every 30 s</span>
      </div>

      {manualModal && (
        <Modal title="Manual GPS Entry (Testing)" onClose={() => setManualModal(false)}>
          <div className="space-y-3">
            <Field label="Vehicle" required>
              <select className={selectCls} value={form.vehicle_id} onChange={e => setForm(f => ({ ...f, vehicle_id: e.target.value }))}>
                <option value="">— Select vehicle —</option>
                {vehicles.map(v => <option key={v.vehicle_id} value={v.vehicle_id}>{v.vehicle_number}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Latitude" required>
                <input type="number" step="0.000001" className={inputCls} value={form.latitude} onChange={e => setForm(f => ({ ...f, latitude: e.target.value }))} placeholder="26.9124" />
              </Field>
              <Field label="Longitude" required>
                <input type="number" step="0.000001" className={inputCls} value={form.longitude} onChange={e => setForm(f => ({ ...f, longitude: e.target.value }))} placeholder="75.7873" />
              </Field>
            </div>
            <Field label="Speed (km/h)">
              <input type="number" className={inputCls} value={form.speed} onChange={e => setForm(f => ({ ...f, speed: e.target.value }))} placeholder="0" />
            </Field>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setManualModal(false)} className="px-4 py-2 text-sm border rounded-lg text-slate-600">Cancel</button>
            <button onClick={saveManual} disabled={saving} className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Update Location'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ANPR & GATES TAB
// ═══════════════════════════════════════════════════════════════════
function AnprGatesTab() {
  const [subTab,     setSubTab]     = useState('gate');
  const [date,       setDate]       = useState(today());
  const [gateLog,    setGateLog]    = useState([]);
  const [anprLog,    setAnprLog]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [addModal,   setAddModal]   = useState(false);
  const [anprModal,  setAnprModal]  = useState(false);
  const [gateForm,   setGateForm]   = useState({ gate_name: 'Main Gate', direction: 'entry', vehicle_number: '', notes: '' });
  const [anprForm,   setAnprForm]   = useState({ detected_plate: '', gate: 'Main Gate', direction: 'entry', confidence_score: 95 });
  const [saving,     setSaving]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [gr, ar] = await Promise.all([
        transportAPI.getGateLog({ date }),
        transportAPI.getAnprLog({ date }),
      ]);
      setGateLog(gr.data);
      setAnprLog(ar.data);
    } catch { toast.error('Failed to load logs'); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const saveGate = async () => {
    if (!gateForm.vehicle_number) return toast.error('Vehicle number required');
    setSaving(true);
    try {
      await transportAPI.createGateEntry(gateForm);
      toast.success('Gate entry recorded');
      setAddModal(false);
      setGateForm({ gate_name: 'Main Gate', direction: 'entry', vehicle_number: '', notes: '' });
      load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const saveAnpr = async () => {
    if (!anprForm.detected_plate) return toast.error('Plate number required');
    setSaving(true);
    try {
      const res = await transportAPI.createAnprEntry(anprForm);
      toast.success(res.data.matched ? 'Plate matched & logged' : 'Plate logged (no match found)');
      setAnprModal(false);
      setAnprForm({ detected_plate: '', gate: 'Main Gate', direction: 'entry', confidence_score: 95 });
      load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const dirBadge = d => (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${d === 'entry' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
      {d === 'entry' ? '→ In' : '← Out'}
    </span>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
          {[{ id: 'gate', label: 'Gate Log' }, { id: 'anpr', label: 'ANPR Log' }].map(t => (
            <button key={t.id} onClick={() => setSubTab(t.id)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${subTab === t.id ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Calendar size={15} className="text-slate-500" />
          <input type="date" className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <button onClick={load} className="text-sm text-blue-600 flex items-center gap-1"><RefreshCw size={13} /> Refresh</button>
        <div className="ml-auto flex gap-2">
          <button onClick={() => setAnprModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50">
            <ScanEye size={14} /> Log ANPR
          </button>
          <button onClick={() => setAddModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Plus size={14} /> Gate Entry
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-blue-500" size={24} /></div>
      ) : (
        <>
          {/* Gate Log */}
          {subTab === 'gate' && (
            <div className="rounded-xl border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-600 uppercase">
                  <tr>{['Time', 'Vehicle No.', 'Type', 'Route', 'Driver', 'Gate', 'Direction', 'Source'].map(h => (
                    <th key={h} className="px-3 py-3 text-left">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {gateLog.map(g => (
                    <tr key={g.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-xs text-slate-500">{g.event_time ? new Date(g.event_time).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}</td>
                      <td className="px-3 py-2 font-semibold text-blue-700">{g.vehicle_number}</td>
                      <td className="px-3 py-2 capitalize text-slate-500 text-xs">{g.vehicle_type || '—'}</td>
                      <td className="px-3 py-2 text-xs">{g.route_name || '—'}</td>
                      <td className="px-3 py-2 text-xs">{g.driver_name || '—'}</td>
                      <td className="px-3 py-2 text-xs">{g.gate_name}</td>
                      <td className="px-3 py-2">{dirBadge(g.direction)}</td>
                      <td className="px-3 py-2 text-xs text-slate-400 capitalize">{g.detection_source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!gateLog.length && <p className="text-center py-12 text-slate-400">No gate events for {date}.</p>}
            </div>
          )}

          {/* ANPR Log */}
          {subTab === 'anpr' && (
            <div className="rounded-xl border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-600 uppercase">
                  <tr>{['Time', 'Detected Plate', 'Matched Vehicle', 'Route', 'Gate', 'Direction', 'Confidence', 'Camera'].map(h => (
                    <th key={h} className="px-3 py-3 text-left">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {anprLog.map(a => (
                    <tr key={a.id} className={`hover:bg-slate-50 ${!a.matched_vehicle_id ? 'bg-orange-50/30' : ''}`}>
                      <td className="px-3 py-2 text-xs text-slate-500">{a.detected_at ? new Date(a.detected_at).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}</td>
                      <td className="px-3 py-2 font-mono font-semibold text-blue-700">{a.detected_plate}</td>
                      <td className="px-3 py-2">
                        {a.matched_number
                          ? <span className="text-green-700 font-medium">{a.matched_number}</span>
                          : <span className="text-orange-600 text-xs">No match</span>}
                      </td>
                      <td className="px-3 py-2 text-xs">{a.route_name || '—'}</td>
                      <td className="px-3 py-2 text-xs">{a.gate || '—'}</td>
                      <td className="px-3 py-2">{dirBadge(a.direction)}</td>
                      <td className="px-3 py-2 text-xs">{a.confidence_score != null ? a.confidence_score + '%' : '—'}</td>
                      <td className="px-3 py-2 text-xs text-slate-400">{a.camera_id || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!anprLog.length && <p className="text-center py-12 text-slate-400">No ANPR events for {date}.</p>}
            </div>
          )}
        </>
      )}

      {/* Gate Entry Modal */}
      {addModal && (
        <Modal title="Manual Gate Entry" onClose={() => setAddModal(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Gate Name">
                <input className={inputCls} value={gateForm.gate_name} onChange={e => setGateForm(f => ({ ...f, gate_name: e.target.value }))} />
              </Field>
              <Field label="Direction">
                <select className={selectCls} value={gateForm.direction} onChange={e => setGateForm(f => ({ ...f, direction: e.target.value }))}>
                  <option value="entry">Entry (In)</option>
                  <option value="exit">Exit (Out)</option>
                </select>
              </Field>
            </div>
            <Field label="Vehicle Number" required>
              <input className={inputCls} value={gateForm.vehicle_number} onChange={e => setGateForm(f => ({ ...f, vehicle_number: e.target.value.toUpperCase() }))} placeholder="RJ14AB1234" />
            </Field>
            <Field label="Notes">
              <input className={inputCls} value={gateForm.notes} onChange={e => setGateForm(f => ({ ...f, notes: e.target.value }))} />
            </Field>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setAddModal(false)} className="px-4 py-2 text-sm border rounded-lg text-slate-600">Cancel</button>
            <button onClick={saveGate} disabled={saving} className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Record Entry'}
            </button>
          </div>
        </Modal>
      )}

      {/* ANPR Log Modal */}
      {anprModal && (
        <Modal title="Log ANPR Detection" onClose={() => setAnprModal(false)}>
          <div className="space-y-3">
            <Field label="Detected Plate" required>
              <input className={inputCls} value={anprForm.detected_plate} onChange={e => setAnprForm(f => ({ ...f, detected_plate: e.target.value.toUpperCase() }))} placeholder="RJ14AB1234" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Gate">
                <input className={inputCls} value={anprForm.gate} onChange={e => setAnprForm(f => ({ ...f, gate: e.target.value }))} />
              </Field>
              <Field label="Direction">
                <select className={selectCls} value={anprForm.direction} onChange={e => setAnprForm(f => ({ ...f, direction: e.target.value }))}>
                  <option value="entry">Entry</option>
                  <option value="exit">Exit</option>
                </select>
              </Field>
            </div>
            <Field label="Confidence Score (%)">
              <input type="number" min="0" max="100" className={inputCls} value={anprForm.confidence_score} onChange={e => setAnprForm(f => ({ ...f, confidence_score: parseInt(e.target.value) || 95 }))} />
            </Field>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setAnprModal(false)} className="px-4 py-2 text-sm border rounded-lg text-slate-600">Cancel</button>
            <button onClick={saveAnpr} disabled={saving} className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Log Detection'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ANALYTICS TAB
// ═══════════════════════════════════════════════════════════════════
const CHART_COLORS = ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#7c3aed', '#0891b2'];

function AnalyticsTab() {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [from,     setFrom]     = useState(() => { const d = new Date(); d.setDate(d.getDate() - 29); return d.toISOString().split('T')[0]; });
  const [to,       setTo]       = useState(today());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await transportAPI.getAnalytics({ from, to });
      setData(res.data);
    } catch { toast.error('Failed to load analytics'); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-blue-500" size={24} /></div>;
  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Date range */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm text-slate-500">Period:</span>
        <input type="date" className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" value={from} onChange={e => setFrom(e.target.value)} />
        <span className="text-slate-400">—</span>
        <input type="date" className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" value={to} onChange={e => setTo(e.target.value)} />
        <button onClick={load} className="flex items-center gap-1 text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700">
          <RefreshCw size={13} /> Apply
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vehicle Trend */}
        {data.vehicle_trend.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-700 text-sm mb-4">Vehicle Attendance Trend</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.vehicle_trend} margin={{ top: 0, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v, n) => [v, n]} labelFormatter={l => `Date: ${l}`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="active" name="Active" fill="#2563eb" radius={[2, 2, 0, 0]} />
                <Bar dataKey="absent" name="Absent" fill="#dc2626" radius={[2, 2, 0, 0]} />
                <Bar dataKey="late"   name="Late"   fill="#d97706" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Student Boarding Trend */}
        {data.boarding_trend.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-700 text-sm mb-4">Student Boarding / Drop Trend</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.boarding_trend} margin={{ top: 0, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="boarded" name="Boarded"   stroke="#2563eb" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="dropped"  name="Dropped"  stroke="#16a34a" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="missed"   name="Missed"   stroke="#dc2626" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Route Utilization */}
        {data.route_utilization.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-700 text-sm mb-4">Route Occupancy</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>{['Route', 'Students', 'Capacity', 'Occupancy'].map(h => <th key={h} className="px-2 py-2 text-left">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.route_utilization.map(r => (
                    <tr key={r.route_code} className="hover:bg-slate-50">
                      <td className="px-2 py-1.5 font-medium text-blue-700">{r.route_code}</td>
                      <td className="px-2 py-1.5">{r.students}</td>
                      <td className="px-2 py-1.5 text-slate-400">{r.capacity || '—'}</td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-slate-200 rounded-full h-1.5">
                            <div className={`h-1.5 rounded-full ${r.occupancy_pct >= 90 ? 'bg-red-500' : r.occupancy_pct >= 70 ? 'bg-amber-500' : 'bg-green-500'}`}
                              style={{ width: `${Math.min(r.occupancy_pct || 0, 100)}%` }} />
                          </div>
                          <span className="text-slate-600">{r.occupancy_pct != null ? r.occupancy_pct + '%' : '—'}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Driver Attendance */}
        {data.driver_stats.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-700 text-sm mb-4">Driver Attendance</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>{['Driver', 'Present', 'Late', 'Absent', 'Attendance %'].map(h => <th key={h} className="px-2 py-2 text-left">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.driver_stats.map((d, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-2 py-1.5 font-medium">{d.driver_name}</td>
                      <td className="px-2 py-1.5 text-green-700">{d.present}</td>
                      <td className="px-2 py-1.5 text-amber-600">{d.late}</td>
                      <td className="px-2 py-1.5 text-red-600">{d.absent}</td>
                      <td className="px-2 py-1.5">
                        <span className={`font-semibold ${d.pct >= 90 ? 'text-green-700' : d.pct >= 75 ? 'text-amber-600' : 'text-red-600'}`}>
                          {d.pct != null ? d.pct + '%' : '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Exception Types */}
        {data.exception_types.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-700 text-sm mb-4">Exception Breakdown</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={data.exception_types.map(e => ({ name: e.type.replace(/_/g, ' '), value: Number(e.cnt) }))}
                  cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name} (${value})`}
                  labelLine={false}>
                  {data.exception_types.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Vehicle Compliance */}
        {data.vehicle_compliance.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-700 text-sm mb-4">Vehicle Compliance</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>{['Vehicle', 'Active Days', 'Absent', 'Compliance'].map(h => <th key={h} className="px-2 py-2 text-left">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.vehicle_compliance.map((v, i) => {
                    const pct = v.tracked > 0 ? Math.round((Number(v.active_days) / Number(v.tracked)) * 100) : null;
                    return (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-2 py-1.5 font-semibold text-blue-700">{v.vehicle_number}</td>
                        <td className="px-2 py-1.5 text-green-700">{v.active_days || 0}</td>
                        <td className="px-2 py-1.5 text-red-600">{v.absent_days || 0}</td>
                        <td className="px-2 py-1.5">
                          <span className={`font-semibold ${pct >= 90 ? 'text-green-700' : pct >= 75 ? 'text-amber-600' : 'text-red-600'}`}>
                            {pct != null ? pct + '%' : '—'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No data fallback */}
        {!data.vehicle_trend.length && !data.route_utilization.length && (
          <div className="col-span-2 text-center py-16 text-slate-400">
            <TrendingUp className="mx-auto mb-3 opacity-30" size={40} />
            <p>No analytics data for the selected period.</p>
            <p className="text-xs mt-1">Record vehicle and student attendance to see charts here.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// EXCEPTIONS TAB
// ═══════════════════════════════════════════════════════════════════
const EXC_TYPES = ['breakdown','accident','late_departure','early_return','route_deviation','student_not_boarded','driver_absent','helper_absent','vehicle_absent','other'];

function ExceptionsTab() {
  const [exceptions, setExceptions] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState('open');
  const [addModal,   setAddModal]   = useState(false);
  const [resolveModal, setResolveModal] = useState(null);
  const [form,       setForm]       = useState({ exception_type: '', vehicle_id: '', description: '' });
  const [resolveNote, setResolveNote] = useState('');
  const [vehicles,   setVehicles]   = useState([]);
  const [saving,     setSaving]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [er, vr] = await Promise.all([
        transportAPI.getExceptions({ status: filter || undefined }),
        transportAPI.getVehicles(),
      ]);
      setExceptions(er.data);
      setVehicles(vr.data);
    } catch { toast.error('Failed to load exceptions'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const saveException = async () => {
    if (!form.exception_type) return toast.error('Exception type required');
    setSaving(true);
    try {
      await transportAPI.createException(form);
      toast.success('Exception raised');
      setAddModal(false);
      setForm({ exception_type: '', vehicle_id: '', description: '' });
      load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const resolve = async () => {
    if (!resolveModal) return;
    setSaving(true);
    try {
      await transportAPI.updateException(resolveModal.id, { status: 'resolved', resolution_notes: resolveNote });
      toast.success('Exception resolved');
      setResolveModal(null);
      setResolveNote('');
      load();
    } catch { toast.error('Failed to resolve'); }
    finally { setSaving(false); }
  };

  const statusColors = {
    open:       'bg-red-100 text-red-800',
    in_progress:'bg-amber-100 text-amber-800',
    resolved:   'bg-green-100 text-green-800',
    closed:     'bg-slate-100 text-slate-600',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
          {[{ v: '', l: 'All (14 days)' }, { v: 'open', l: 'Open' }, { v: 'in_progress', l: 'In Progress' }, { v: 'resolved', l: 'Resolved' }].map(o => (
            <button key={o.v} onClick={() => setFilter(o.v)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${filter === o.v ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600'}`}>
              {o.l}
            </button>
          ))}
        </div>
        <button onClick={load} className="text-sm text-blue-600 flex items-center gap-1"><RefreshCw size={13} /> Refresh</button>
        <button onClick={() => setAddModal(true)} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">
          <Plus size={14} /> Raise Exception
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-blue-500" size={24} /></div>
      ) : (
        <div className="rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600 uppercase">
              <tr>{['Date', 'Type', 'Vehicle', 'Driver', 'Route', 'Description', 'Status', 'Actions'].map(h => (
                <th key={h} className="px-3 py-3 text-left">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {exceptions.map(e => (
                <tr key={e.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 text-xs text-slate-500">{e.exception_date}</td>
                  <td className="px-3 py-2">
                    <span className="px-2 py-0.5 rounded bg-red-50 text-red-700 text-xs font-medium">
                      {e.exception_type?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-medium text-blue-700 text-xs">{e.vehicle_number || '—'}</td>
                  <td className="px-3 py-2 text-xs">{e.driver_name || '—'}</td>
                  <td className="px-3 py-2 text-xs">{e.route_name || '—'}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 max-w-xs truncate">{e.description || '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[e.status] || 'bg-slate-100 text-slate-600'}`}>
                      {e.status?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {e.status !== 'resolved' && e.status !== 'closed' && (
                      <button onClick={() => setResolveModal(e)} className="text-xs text-green-700 hover:underline">Resolve</button>
                    )}
                    {e.resolution_notes && (
                      <span className="ml-2 text-xs text-slate-400" title={e.resolution_notes}>📝</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!exceptions.length && <p className="text-center py-12 text-slate-400">No exceptions found for current filter.</p>}
        </div>
      )}

      {/* Add Exception Modal */}
      {addModal && (
        <Modal title="Raise Exception" onClose={() => setAddModal(false)}>
          <div className="space-y-3">
            <Field label="Exception Type" required>
              <select className={selectCls} value={form.exception_type} onChange={e => setForm(f => ({ ...f, exception_type: e.target.value }))}>
                <option value="">— Select type —</option>
                {EXC_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </Field>
            <Field label="Vehicle">
              <select className={selectCls} value={form.vehicle_id} onChange={e => setForm(f => ({ ...f, vehicle_id: e.target.value }))}>
                <option value="">— None / General —</option>
                {vehicles.filter(v => v.status === 'active').map(v => <option key={v.id} value={v.id}>{v.vehicle_number}</option>)}
              </select>
            </Field>
            <Field label="Description">
              <textarea className={inputCls} rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the exception…" />
            </Field>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setAddModal(false)} className="px-4 py-2 text-sm border rounded-lg text-slate-600">Cancel</button>
            <button onClick={saveException} disabled={saving} className="px-5 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
              {saving ? 'Raising…' : 'Raise Exception'}
            </button>
          </div>
        </Modal>
      )}

      {/* Resolve Modal */}
      {resolveModal && (
        <Modal title="Resolve Exception" onClose={() => setResolveModal(null)}>
          <div className="mb-3 p-3 bg-slate-50 rounded-lg">
            <p className="text-sm font-medium">{resolveModal.exception_type?.replace(/_/g, ' ')}</p>
            <p className="text-xs text-slate-500 mt-0.5">{resolveModal.description || 'No description'}</p>
          </div>
          <Field label="Resolution Notes">
            <textarea className={inputCls} rows={3} value={resolveNote} onChange={e => setResolveNote(e.target.value)} placeholder="Describe how the issue was resolved…" />
          </Field>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setResolveModal(null)} className="px-4 py-2 text-sm border rounded-lg text-slate-600">Cancel</button>
            <button onClick={resolve} disabled={saving} className="px-5 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
              {saving ? 'Resolving…' : 'Mark Resolved'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// REPORTS TAB
// ═══════════════════════════════════════════════════════════════════
function ReportsTab() {
  const [activeReport, setActiveReport] = useState('daily');
  const [date,    setDate]    = useState(today());
  const [year,    setYear]    = useState(new Date().getFullYear());
  const [month,   setMonth]   = useState(new Date().getMonth() + 1);
  const [from,    setFrom]    = useState(() => { const d=new Date(); d.setDate(1); return d.toISOString().split('T')[0]; });
  const [to,      setTo]      = useState(today());
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const REPORTS = [
    { id: 'daily',      label: 'Daily Attendance' },
    { id: 'route',      label: 'Route Summary' },
    { id: 'monthly',    label: 'Monthly Report' },
    { id: 'compliance', label: 'Vehicle Compliance' },
    { id: 'students',   label: 'Student List' },
  ];

  const load = async () => {
    setLoading(true); setData(null);
    try {
      let res;
      if (activeReport === 'daily')      res = await transportAPI.getDailyReport({ date });
      else if (activeReport === 'route') res = await transportAPI.getRouteReport({ date });
      else if (activeReport === 'monthly') res = await transportAPI.getMonthlyReport({ year, month });
      else if (activeReport === 'compliance') res = await transportAPI.getVehicleComplianceReport({ from, to });
      else if (activeReport === 'students')   res = await transportAPI.getStudentTransportReport();
      setData(res.data);
    } catch { toast.error('Failed to generate report'); }
    finally { setLoading(false); }
  };

  // Excel export using SheetJS (xlsx already installed in the project)
  const exportExcel = async () => {
    if (!data) return;
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();

      if (activeReport === 'daily') {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.vehicles || []), 'Vehicles');
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.students || []), 'Students');
      } else if (activeReport === 'route') {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.routes   || []), 'Routes');
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.students || []), 'Students');
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.stops    || []), 'Stops');
      } else if (activeReport === 'monthly') {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.vehicle_summary || []), 'Vehicles');
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.driver_summary  || []), 'Drivers');
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.student_summary || []), 'Students');
      } else if (activeReport === 'compliance') {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.vehicles || []), 'Compliance');
      } else if (activeReport === 'students') {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.students || []), 'Students');
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data.route_summary || []), 'Route Summary');
      }

      const label = REPORTS.find(r => r.id === activeReport)?.label || activeReport;
      XLSX.writeFile(wb, `Transport_${label.replace(/\s+/g,'_')}_${today()}.xlsx`);
      toast.success('Excel file downloaded');
    } catch (e) { toast.error('Export failed: ' + e.message); }
  };

  const printReport = () => window.print();

  // Summary cards for daily report
  const DailySummary = () => {
    if (!data?.summary) return null;
    const s = data.summary;
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        {[
          { label: 'Total Vehicles', value: s.total_vehicles, color: 'blue' },
          { label: 'Vehicles Active', value: s.vehicles_active, color: 'green' },
          { label: 'Students Mapped', value: s.students_mapped, color: 'slate' },
          { label: 'Students Boarded', value: s.students_boarded, color: 'green' },
          { label: 'Students Dropped', value: s.students_dropped, color: 'blue' },
          { label: 'Missed (Total)', value: s.missed_total, color: s.missed_total > 0 ? 'red' : 'green' },
        ].map(c => <KpiCard key={c.label} {...c} small icon={Bus} />)}
      </div>
    );
  };

  const tableWrap = (headers, rows, keyFn) => (
    <div className="overflow-x-auto rounded-xl border border-slate-200 mt-3">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-600 uppercase">
          <tr>{headers.map(h => <th key={h} className="px-3 py-2.5 text-left">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={keyFn ? keyFn(r) : i} className="hover:bg-slate-50">
              {headers.map(h => {
                const key = h.toLowerCase().replace(/[\s/()]+/g, '_').replace(/_+$/,'');
                const val = r[Object.keys(r).find(k => k.toLowerCase().replace(/\s/g,'_') === key)] ?? r[key] ?? '—';
                return <td key={h} className="px-3 py-2 text-slate-700">{String(val ?? '—')}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && <p className="text-center py-8 text-slate-400">No data.</p>}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Report selector */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl flex-wrap">
        {REPORTS.map(r => (
          <button key={r.id} onClick={() => { setActiveReport(r.id); setData(null); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${activeReport === r.id ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}>
            {r.label}
          </button>
        ))}
      </div>

      {/* Filter row */}
      <div className="flex items-center gap-3 flex-wrap">
        {(activeReport === 'daily' || activeReport === 'route') && (
          <div className="flex items-center gap-2">
            <Calendar size={15} className="text-slate-500" />
            <input type="date" className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" value={date} onChange={e => setDate(e.target.value)} />
          </div>
        )}
        {activeReport === 'monthly' && (
          <div className="flex items-center gap-2">
            <select className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" value={month} onChange={e => setMonth(Number(e.target.value))}>
              {MONTHS.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
            </select>
            <select className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" value={year} onChange={e => setYear(Number(e.target.value))}>
              {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        )}
        {activeReport === 'compliance' && (
          <>
            <input type="date" className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" value={from} onChange={e => setFrom(e.target.value)} />
            <span className="text-slate-400">—</span>
            <input type="date" className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" value={to} onChange={e => setTo(e.target.value)} />
          </>
        )}
        <button onClick={load} disabled={loading} className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          {loading ? 'Generating…' : 'Generate Report'}
        </button>
        {data && (
          <div className="ml-auto flex gap-2">
            <button onClick={exportExcel} className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-green-600 text-green-700 rounded-lg hover:bg-green-50">
              ⬇ Excel
            </button>
            <button onClick={printReport} className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 text-slate-600 rounded-lg hover:bg-slate-50">
              🖨 Print
            </button>
          </div>
        )}
      </div>

      {/* Report content */}
      {loading && <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-blue-500" size={24} /></div>}

      {data && activeReport === 'daily' && (
        <div>
          <DailySummary />
          <h3 className="font-semibold text-slate-700 text-sm mt-4 mb-2">Vehicle-wise Status — {data.date}</h3>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600 uppercase">
                <tr>{['Vehicle','Type','Route','Driver','Helper','Status','Departure','Return','Boarded','Missed'].map(h=><th key={h} className="px-3 py-2.5 text-left">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data.vehicles||[]).map(v=>(
                  <tr key={v.vehicle_number} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-semibold text-blue-700">{v.vehicle_number}</td>
                    <td className="px-3 py-2 capitalize">{v.vehicle_type}</td>
                    <td className="px-3 py-2">{v.route_name||'—'}</td>
                    <td className="px-3 py-2">{v.driver_name||'—'}</td>
                    <td className="px-3 py-2">{v.helper_name||'—'}</td>
                    <td className="px-3 py-2"><Badge label={v.v_status||'not_reported'} /></td>
                    <td className="px-3 py-2 text-slate-500">{v.departure_time?new Date(v.departure_time).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}):'—'}</td>
                    <td className="px-3 py-2 text-slate-500">{v.return_time?new Date(v.return_time).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}):'—'}</td>
                    <td className="px-3 py-2 text-green-700 font-semibold">{v.students_boarded||0}</td>
                    <td className="px-3 py-2 text-red-600 font-semibold">{v.students_missed||0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!(data.vehicles||[]).length && <p className="text-center py-8 text-slate-400">No vehicle data.</p>}
          </div>

          <h3 className="font-semibold text-slate-700 text-sm mt-5 mb-2">Student Attendance — {data.date}</h3>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600 uppercase">
                <tr>{['Student','Class','Adm No','Father','Phone','Route','Vehicle','Pickup Stop','Boarding','Drop Stop','Drop'].map(h=><th key={h} className="px-3 py-2.5 text-left">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data.students||[]).map((s,i)=>(
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium">{s.name}</td>
                    <td className="px-3 py-2">{s.class_name} {s.section}</td>
                    <td className="px-3 py-2 text-slate-400">{s.admission_no}</td>
                    <td className="px-3 py-2">{s.father_name||'—'}</td>
                    <td className="px-3 py-2 text-slate-500">{s.father_phone||'—'}</td>
                    <td className="px-3 py-2 text-blue-700 font-medium">{s.route_code}</td>
                    <td className="px-3 py-2">{s.vehicle_number||'—'}</td>
                    <td className="px-3 py-2">{s.pickup_stop||'—'}</td>
                    <td className="px-3 py-2"><Badge label={s.boarding_status||'—'} /></td>
                    <td className="px-3 py-2">{s.drop_stop||'—'}</td>
                    <td className="px-3 py-2"><Badge label={s.drop_status||'—'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!(data.students||[]).length && <p className="text-center py-8 text-slate-400">No student attendance data.</p>}
          </div>
        </div>
      )}

      {data && activeReport === 'route' && (
        <div>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600 uppercase">
                <tr>{['Code','Route Name','Vehicle','Driver','Students','Stops','Distance'].map(h=><th key={h} className="px-3 py-2.5 text-left">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data.routes||[]).map(r=>(
                  <tr key={r.route_code} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-semibold text-blue-700">{r.route_code}</td>
                    <td className="px-3 py-2 font-medium">{r.route_name}</td>
                    <td className="px-3 py-2">{r.vehicle_number||'—'}</td>
                    <td className="px-3 py-2">{r.driver_name||'—'}</td>
                    <td className="px-3 py-2 text-center">{r.students_mapped}</td>
                    <td className="px-3 py-2 text-center">{r.stops_count}</td>
                    <td className="px-3 py-2">{r.distance_km?r.distance_km+' km':'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <h3 className="font-semibold text-slate-700 text-sm mt-5 mb-2">Students by Route</h3>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600 uppercase">
                <tr>{['Student','Class','Route','Vehicle','Pickup Stop','Time','Boarding','Drop'].map(h=><th key={h} className="px-3 py-2.5 text-left">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data.students||[]).map((s,i)=>(
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium">{s.name}</td>
                    <td className="px-3 py-2">{s.class_name} {s.section}</td>
                    <td className="px-3 py-2 text-blue-700">{s.route_code}</td>
                    <td className="px-3 py-2">{s.vehicle_number||'—'}</td>
                    <td className="px-3 py-2">{s.pickup_stop||'—'}</td>
                    <td className="px-3 py-2 text-slate-500">{s.pickup_time||'—'}</td>
                    <td className="px-3 py-2"><Badge label={s.boarding_status||'—'} /></td>
                    <td className="px-3 py-2"><Badge label={s.drop_status||'—'} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && activeReport === 'monthly' && (
        <div className="space-y-5">
          <h3 className="font-semibold text-slate-700">Monthly Report — {MONTHS[data.period.month-1]} {data.period.year}</h3>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Vehicle Summary</p>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase">
                  <tr>{['Vehicle','Type','Days Tracked','Active Days','Absent','Late','Compliance %'].map(h=><th key={h} className="px-3 py-2.5 text-left">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(data.vehicle_summary||[]).map(v=>(
                    <tr key={v.vehicle_number} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-semibold text-blue-700">{v.vehicle_number}</td>
                      <td className="px-3 py-2 capitalize">{v.vehicle_type}</td>
                      <td className="px-3 py-2 text-center">{v.days_tracked}</td>
                      <td className="px-3 py-2 text-center text-green-700">{v.days_active||0}</td>
                      <td className="px-3 py-2 text-center text-red-600">{v.days_absent||0}</td>
                      <td className="px-3 py-2 text-center text-amber-600">{v.days_late||0}</td>
                      <td className="px-3 py-2 font-semibold">
                        <span className={v.compliance_pct>=90?'text-green-700':v.compliance_pct>=75?'text-amber-600':'text-red-600'}>
                          {v.compliance_pct!=null?v.compliance_pct+'%':'—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Driver Summary</p>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase">
                  <tr>{['Driver','Code','Present','Absent','Late','Attendance %'].map(h=><th key={h} className="px-3 py-2.5 text-left">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(data.driver_summary||[]).map((d,i)=>(
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium">{d.name}</td>
                      <td className="px-3 py-2 text-slate-400">{d.employee_code}</td>
                      <td className="px-3 py-2 text-green-700">{d.present||0}</td>
                      <td className="px-3 py-2 text-red-600">{d.absent||0}</td>
                      <td className="px-3 py-2 text-amber-600">{d.late||0}</td>
                      <td className="px-3 py-2 font-semibold">
                        <span className={d.pct>=90?'text-green-700':d.pct>=75?'text-amber-600':'text-red-600'}>
                          {d.pct!=null?d.pct+'%':'—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {data && activeReport === 'compliance' && (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600 uppercase">
              <tr>{['Vehicle','Type','Route','Driver','Active','Absent','Late','Compliance %','Insurance Expiry','Fitness Expiry','Pollution Expiry'].map(h=><th key={h} className="px-3 py-2.5 text-left">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data.vehicles||[]).map(v=>{
                const expWarn = (d) => d && new Date(d)<new Date(Date.now()+30*864e5) ? 'text-red-600 font-semibold' : 'text-slate-600';
                return (
                  <tr key={v.vehicle_number} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-semibold text-blue-700">{v.vehicle_number}</td>
                    <td className="px-3 py-2 capitalize">{v.vehicle_type}</td>
                    <td className="px-3 py-2">{v.route_name||'—'}</td>
                    <td className="px-3 py-2">{v.driver_name||'—'}</td>
                    <td className="px-3 py-2 text-green-700">{v.days_active||0}</td>
                    <td className="px-3 py-2 text-red-600">{v.days_absent||0}</td>
                    <td className="px-3 py-2 text-amber-600">{v.days_late||0}</td>
                    <td className="px-3 py-2 font-semibold">
                      <span className={v.compliance_pct>=90?'text-green-700':v.compliance_pct>=75?'text-amber-600':'text-red-600'}>
                        {v.compliance_pct!=null?v.compliance_pct+'%':'—'}
                      </span>
                    </td>
                    <td className={`px-3 py-2 ${expWarn(v.insurance_expiry)}`}>{v.insurance_expiry||'—'}</td>
                    <td className={`px-3 py-2 ${expWarn(v.fitness_expiry)}`}>{v.fitness_expiry||'—'}</td>
                    <td className={`px-3 py-2 ${expWarn(v.pollution_expiry)}`}>{v.pollution_expiry||'—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!(data.vehicles||[]).length && <p className="text-center py-8 text-slate-400">No data.</p>}
        </div>
      )}

      {data && activeReport === 'students' && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {(data.route_summary||[]).map(r=>(
              <div key={r.route_code} className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                <p className="font-bold text-blue-700 text-lg">{r.students}</p>
                <p className="text-xs text-blue-600">{r.route_code} – {r.route_name}</p>
                {r.total_fee>0 && <p className="text-xs text-slate-500">Fee: ₹{Number(r.total_fee).toLocaleString('en-IN')}</p>}
              </div>
            ))}
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600 uppercase">
                <tr>{['Student','Class','Adm No','Father','Phone','Route','Vehicle','Pickup Stop','Drop Stop','Type','Fee'].map(h=><th key={h} className="px-3 py-2.5 text-left">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(data.students||[]).map((s,i)=>(
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium">{s.name}</td>
                    <td className="px-3 py-2">{s.class_name} {s.section}</td>
                    <td className="px-3 py-2 text-slate-400">{s.admission_no}</td>
                    <td className="px-3 py-2">{s.father_name||'—'}</td>
                    <td className="px-3 py-2 text-slate-500">{s.father_phone||'—'}</td>
                    <td className="px-3 py-2 text-blue-700 font-medium">{s.route_code}</td>
                    <td className="px-3 py-2">{s.vehicle_number||'—'}</td>
                    <td className="px-3 py-2">{s.pickup_stop||'—'}</td>
                    <td className="px-3 py-2">{s.drop_stop||'—'}</td>
                    <td className="px-3 py-2 capitalize">{s.transport_type||'—'}</td>
                    <td className="px-3 py-2">{s.fee_amount?'₹'+Number(s.fee_amount).toLocaleString('en-IN'):'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!(data.students||[]).length && <p className="text-center py-8 text-slate-400">No students mapped to transport.</p>}
          </div>
        </div>
      )}

      {!data && !loading && (
        <div className="text-center py-20 text-slate-400">
          <Map className="mx-auto mb-3 opacity-30" size={44} />
          <p className="text-sm">Select a report type and click <b>Generate Report</b>.</p>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// NOTIFICATIONS TAB
// ═══════════════════════════════════════════════════════════════════
function NotificationsTab() {
  const [subTab,    setSubTab]    = useState('log');
  const [log,       setLog]       = useState([]);
  const [settings,  setSettings]  = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [testForm,  setTestForm]  = useState({ method: 'sms', phone: '', chat_id: '' });
  const [testResult,setTestResult]= useState(null);
  const [date,      setDate]      = useState(today());

  const loadLog = useCallback(async () => {
    setLoading(true);
    try { const r = await transportAPI.getNotificationLog({ date }); setLog(r.data); }
    catch { toast.error('Failed to load log'); }
    finally { setLoading(false); }
  }, [date]);

  const loadSettings = useCallback(async () => {
    try { const r = await transportAPI.getNotificationSettings(); setSettings(r.data); }
    catch {}
  }, []);

  useEffect(() => { loadLog(); loadSettings(); }, [loadLog, loadSettings]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await transportAPI.updateNotificationSettings(settings);
      toast.success('Settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setSaving(true); setTestResult(null);
    try {
      const r = await transportAPI.sendTestNotification(testForm);
      setTestResult(r.data);
      toast.success(r.data.message);
    } catch (e) { toast.error('Test failed'); setTestResult({ status: 'failed' }); }
    finally { setSaving(false); }
  };

  const statusBadge = s => ({
    sent:    'bg-green-100 text-green-800',
    failed:  'bg-red-100 text-red-700',
    pending: 'bg-amber-100 text-amber-800',
    skipped: 'bg-slate-100 text-slate-600',
  }[s] || 'bg-slate-100 text-slate-600');

  const setf = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  return (
    <div className="space-y-4">
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {[{ id:'log', label:'Notification Log'}, {id:'settings', label:'Settings'}, {id:'test', label:'Send Test'}].map(t=>(
          <button key={t.id} onClick={()=>setSubTab(t.id)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${subTab===t.id?'bg-white text-blue-700 shadow-sm':'text-slate-600'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Notification Log */}
      {subTab === 'log' && (
        <div>
          <div className="flex items-center gap-3 mb-3">
            <Calendar size={15} className="text-slate-500" />
            <input type="date" className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" value={date} onChange={e=>setDate(e.target.value)} />
            <button onClick={loadLog} className="text-sm text-blue-600 flex items-center gap-1"><RefreshCw size={13}/> Refresh</button>
          </div>
          {loading ? <div className="flex justify-center py-12"><RefreshCw className="animate-spin text-blue-500" size={22}/></div> : (
            <div className="rounded-xl border border-slate-200 overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase">
                  <tr>{['Time','Student','Event','Method','Recipient','Status','Error'].map(h=><th key={h} className="px-3 py-2.5 text-left">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {log.map(n=>(
                    <tr key={n.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-slate-400">{n.created_at?new Date(n.created_at).toLocaleTimeString():'—'}</td>
                      <td className="px-3 py-2 font-medium">{n.student_name||'—'}</td>
                      <td className="px-3 py-2">{(n.event_type||'—').replace(/_/g,' ')}</td>
                      <td className="px-3 py-2 uppercase text-slate-500">{n.method}</td>
                      <td className="px-3 py-2 font-mono">{n.recipient}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(n.status)}`}>{n.status}</span>
                      </td>
                      <td className="px-3 py-2 text-red-500 text-xs max-w-xs truncate">{n.error_message||''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!log.length && <p className="text-center py-10 text-slate-400">No notifications sent on {date}.</p>}
            </div>
          )}
        </div>
      )}

      {/* Settings */}
      {subTab === 'settings' && settings && (
        <div className="max-w-2xl space-y-5">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
            <b>Setup required:</b> Add <code className="bg-amber-100 px-1 rounded">SMS_API_KEY</code> (Fast2SMS) to <code className="bg-amber-100 px-1 rounded">backend/.env</code> for SMS notifications. For Telegram, use the existing <code className="bg-amber-100 px-1 rounded">TELEGRAM_BOT_TOKEN</code>.
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-slate-700">Enable Parent Notifications</p>
                <p className="text-xs text-slate-400 mt-0.5">Send SMS/Telegram to parents on boarding/drop events</p>
              </div>
              <button onClick={()=>setf('enabled',!settings.enabled)}
                className={`relative w-11 h-6 rounded-full transition-colors ${settings.enabled?'bg-blue-600':'bg-slate-300'}`}>
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${settings.enabled?'left-5':'left-0.5'}`}/>
              </button>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium text-slate-600 mb-3">Delivery Method</p>
              <div className="flex gap-3">
                {['sms','telegram'].map(m=>(
                  <label key={m} className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer ${settings.method===m?'border-blue-500 bg-blue-50 text-blue-700':'border-slate-200 text-slate-600'}`}>
                    <input type="radio" className="sr-only" checked={settings.method===m} onChange={()=>setf('method',m)} />
                    <span className="text-sm font-medium uppercase">{m}</span>
                  </label>
                ))}
              </div>
              {settings.method==='telegram' && (
                <div className="mt-3">
                  <Field label="Telegram Chat ID (Admin / Parent Group)">
                    <input className={inputCls} value={settings.telegram_chat_id||''} onChange={e=>setf('telegram_chat_id',e.target.value)} placeholder="-100123456789" />
                  </Field>
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium text-slate-600 mb-3">Notification Events</p>
              {[
                {key:'notify_boarding',      label:'Student Boarded Bus'},
                {key:'notify_drop',          label:'Student Dropped at Stop'},
                {key:'notify_missed_pickup', label:'Student Missed Pickup (Alert)'},
                {key:'notify_missed_drop',   label:'Student Missed Drop (Alert)'},
              ].map(opt=>(
                <div key={opt.key} className="flex items-center justify-between py-1.5">
                  <span className="text-sm text-slate-600">{opt.label}</span>
                  <button onClick={()=>setf(opt.key,!settings[opt.key])}
                    className={`relative w-9 h-5 rounded-full transition-colors ${settings[opt.key]?'bg-blue-600':'bg-slate-300'}`}>
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${settings[opt.key]?'left-4':'left-0.5'}`}/>
                  </button>
                </div>
              ))}
            </div>

            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-medium text-slate-600">Message Templates</p>
              <p className="text-xs text-slate-400">Variables: <code>{'{{name}}'}</code> <code>{'{{vehicle}}'}</code> <code>{'{{route}}'}</code> <code>{'{{stop}}'}</code> <code>{'{{time}}'}</code> <code>{'{{date}}'}</code></p>
              {[
                {key:'boarding_template',      label:'Boarding Message'},
                {key:'drop_template',          label:'Drop Message'},
                {key:'missed_pickup_template', label:'Missed Pickup Alert'},
                {key:'missed_drop_template',   label:'Missed Drop Alert'},
              ].map(t=>(
                <Field key={t.key} label={t.label}>
                  <textarea className={inputCls} rows={2} value={settings[t.key]||''} onChange={e=>setf(t.key,e.target.value)} />
                </Field>
              ))}
              <Field label="School Phone (for alerts)">
                <input className={inputCls} value={settings.school_phone||''} onChange={e=>setf('school_phone',e.target.value)} />
              </Field>
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={saveSettings} disabled={saving} className="px-6 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </div>
      )}

      {/* Test */}
      {subTab === 'test' && (
        <div className="max-w-md space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
            <p className="text-sm text-slate-600">Send a test message to verify your notification setup.</p>
            <Field label="Method">
              <select className={selectCls} value={testForm.method} onChange={e=>setTestForm(f=>({...f,method:e.target.value}))}>
                <option value="sms">SMS (Fast2SMS)</option>
                <option value="telegram">Telegram Bot</option>
              </select>
            </Field>
            {testForm.method === 'sms' ? (
              <Field label="Phone Number (10 digits)" required>
                <input className={inputCls} value={testForm.phone} onChange={e=>setTestForm(f=>({...f,phone:e.target.value}))} placeholder="9876543210" />
              </Field>
            ) : (
              <Field label="Telegram Chat ID" required>
                <input className={inputCls} value={testForm.chat_id} onChange={e=>setTestForm(f=>({...f,chat_id:e.target.value}))} placeholder="-100123456789" />
              </Field>
            )}
            <button onClick={sendTest} disabled={saving} className="w-full py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Sending…' : 'Send Test Notification'}
            </button>
            {testResult && (
              <div className={`p-3 rounded-lg text-sm ${testResult.status==='sent'?'bg-green-50 text-green-800 border border-green-200':'bg-red-50 text-red-800 border border-red-200'}`}>
                {testResult.message || testResult.status}
              </div>
            )}
          </div>
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 text-xs text-slate-500 space-y-1">
            <p><b>SMS Setup:</b> Add <code>SMS_API_KEY=your_key</code> to <code>backend/.env</code>. Get API key from fast2sms.com.</p>
            <p><b>Telegram Setup:</b> The existing bot token works. Add the bot to the target group and get the chat ID via <code>getUpdates</code>.</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ROUTE OPTIMIZATION TAB
// ═══════════════════════════════════════════════════════════════════
const SUGGESTION_TYPE_LABEL = {
  vehicle_upgrade:  { label: 'Upgrade Vehicle',  color: 'bg-red-100 text-red-800' },
  vehicle_downgrade:{ label: 'Downsize Vehicle', color: 'bg-amber-100 text-amber-800' },
  route_merge:      { label: 'Merge Route',      color: 'bg-orange-100 text-orange-800' },
  reduce_time:      { label: 'Review Route',     color: 'bg-yellow-100 text-yellow-800' },
  merge_stops:      { label: 'Merge Stops',      color: 'bg-blue-100 text-blue-800' },
  add_stop:         { label: 'Add Stop',         color: 'bg-green-100 text-green-800' },
};

function RouteOptimizationTab() {
  const [suggestions,  setSuggestions]  = useState([]);
  const [efficiency,   setEfficiency]   = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [generating,   setGenerating]   = useState(false);
  const [filter,       setFilter]       = useState('pending');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sr, er] = await Promise.all([
        transportAPI.getOptimizationSuggestions({ status: filter || undefined }),
        transportAPI.getRouteEfficiency(),
      ]);
      setSuggestions(sr.data);
      setEfficiency(er.data);
    } catch { toast.error('Failed to load optimization data'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await transportAPI.generateOptimizationSuggestions();
      toast.success(`${res.data.generated} suggestions generated`);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await transportAPI.updateOptimizationSuggestion(id, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch { toast.error('Failed to update'); }
  };

  const scoreColor = s => s >= 80 ? 'text-red-600' : s >= 60 ? 'text-amber-600' : 'text-green-600';

  return (
    <div>
      {/* Header row */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-semibold text-slate-800">Route Optimization Suggestions</h2>
          <p className="text-xs text-slate-500 mt-0.5">AI-generated heuristic analysis of your transport network</p>
        </div>
        <div className="flex items-center gap-3">
          <select className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" value={filter} onChange={e=>setFilter(e.target.value)}>
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>
          <button onClick={generate} disabled={generating}
            className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50">
            <Zap size={15} /> {generating ? 'Analysing…' : 'Run Analysis'}
          </button>
        </div>
      </div>

      {/* Efficiency KPIs */}
      {efficiency && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
          <KpiCard label="Active Routes"    value={efficiency.totals?.active_routes ?? '—'}   icon={Route}      color="blue" small />
          <KpiCard label="Students Served"  value={efficiency.totals?.students_served ?? '—'}  icon={Users}      color="green" small />
          <KpiCard label="Total Distance"   value={efficiency.totals?.total_distance_km ? `${efficiency.totals.total_distance_km} km` : '—'} icon={Map} color="amber" small />
          <KpiCard label="Pending Actions"  value={efficiency.pending_suggestions ?? 0}        icon={AlertTriangle} color={efficiency.pending_suggestions > 0 ? 'red' : 'slate'} small />
        </div>
      )}

      {/* Route efficiency table */}
      {efficiency?.routes?.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Route Efficiency Overview</h3>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600 uppercase">
                <tr>{['Code','Route','Vehicle','Students','Capacity','Occupancy','Stops','Km/Stop','Students/Km'].map(h=><th key={h} className="px-3 py-2.5 text-left">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {efficiency.routes.map(r => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-semibold text-blue-700">{r.route_code}</td>
                    <td className="px-3 py-2 font-medium">{r.route_name}</td>
                    <td className="px-3 py-2">{r.vehicle_number||'—'}</td>
                    <td className="px-3 py-2 text-center">{r.students_mapped}</td>
                    <td className="px-3 py-2 text-center">{r.seating_capacity||'—'}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`font-semibold ${r.occupancy_pct >= 90 ? 'text-red-600' : r.occupancy_pct < 35 ? 'text-amber-600' : 'text-green-600'}`}>
                        {r.occupancy_pct ?? '—'}%
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">{r.stop_count}</td>
                    <td className="px-3 py-2 text-center">{r.km_per_stop??'—'}</td>
                    <td className="px-3 py-2 text-center">{r.students_per_km??'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Suggestions list */}
      <h3 className="text-sm font-semibold text-slate-700 mb-3">
        {suggestions.length} suggestion{suggestions.length !== 1 ? 's' : ''}
        {filter ? ` (${filter})` : ''}
      </h3>

      {loading ? (
        <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-blue-500" size={24} /></div>
      ) : suggestions.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Lightbulb size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">No suggestions yet</p>
          <p className="text-sm mt-1">Click "Run Analysis" to generate AI recommendations.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {suggestions.map(s => {
            const typeInfo = SUGGESTION_TYPE_LABEL[s.suggestion_type] || { label: s.suggestion_type, color: 'bg-slate-100 text-slate-600' };
            return (
              <div key={s.id} className={`rounded-xl border p-4 ${s.status === 'accepted' ? 'border-green-200 bg-green-50' : s.status === 'rejected' ? 'border-slate-200 bg-slate-50 opacity-60' : 'border-slate-200 bg-white'}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${typeInfo.color}`}>{typeInfo.label}</span>
                      {s.route_code && <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full font-mono">{s.route_code}</span>}
                      <span className={`text-xs font-bold ${scoreColor(s.optimization_score)}`}>Score: {s.optimization_score}</span>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">{s.description}</p>
                    <div className="flex gap-4 mt-2 text-xs text-slate-500">
                      {s.estimated_time_saved && <span>⏱ {s.estimated_time_saved} min saved</span>}
                      {s.estimated_distance_saved && <span>📍 {s.estimated_distance_saved} km saved</span>}
                      {s.estimated_fuel_saved && <span>⛽ {s.estimated_fuel_saved} L fuel saved</span>}
                    </div>
                  </div>
                  {s.status === 'pending' && (
                    <div className="flex gap-2 shrink-0">
                      <button onClick={()=>updateStatus(s.id,'accepted')}
                        className="text-xs px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">Accept</button>
                      <button onClick={()=>updateStatus(s.id,'rejected')}
                        className="text-xs px-3 py-1.5 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 font-medium">Dismiss</button>
                    </div>
                  )}
                  {s.status !== 'pending' && (
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium shrink-0 ${s.status === 'accepted' ? 'bg-green-200 text-green-800' : 'bg-slate-200 text-slate-600'}`}>
                      {s.status}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// FACE & ANPR RECOGNITION TAB
// ═══════════════════════════════════════════════════════════════════
function ImageUploadBox({ label, onImage, preview, disabled }) {
  const ref = React.useRef();
  const handleFile = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => onImage(ev.target.result);
    reader.readAsDataURL(file);
  };
  return (
    <div
      className={`relative border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer transition-colors
        ${preview ? 'border-blue-400 bg-blue-50' : 'border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      style={{ minHeight: 160 }}
      onClick={() => !disabled && ref.current.click()}
    >
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={disabled} />
      {preview
        ? <img src={preview} alt="preview" className="max-h-40 rounded-lg object-contain" />
        : <><Upload size={28} className="text-slate-400 mb-2" /><p className="text-sm text-slate-500">{label}</p></>
      }
    </div>
  );
}

function RecognitionTab() {
  const [subTab,       setSubTab]       = useState('recognize');
  const [serviceUp,    setServiceUp]    = useState(null);
  const [enrolledFaces,setEnrolledFaces]= useState([]);
  const [employees,    setEmployees]    = useState([]);

  // Recognize state
  const [recType,      setRecType]      = useState('plate');
  const [recImage,     setRecImage]     = useState(null);
  const [recLoading,   setRecLoading]   = useState(false);
  const [recResult,    setRecResult]    = useState(null);

  // Enroll state
  const [enrollCat,    setEnrollCat]    = useState('driver');
  const [enrollName,   setEnrollName]   = useState('');
  const [enrollId,     setEnrollId]     = useState('');
  const [enrollImage,  setEnrollImage]  = useState(null);
  const [enrollLoading,setEnrollLoading]= useState(false);

  // Log state
  const [log,          setLog]          = useState([]);
  const [logLoading,   setLogLoading]   = useState(false);
  const [logType,      setLogType]      = useState('');

  const checkHealth = useCallback(async () => {
    try {
      const r = await transportAPI.recognitionHealth();
      setServiceUp(r.data);
    } catch { setServiceUp({ online: false }); }
  }, []);

  const loadFaces = useCallback(async () => {
    try { const r = await transportAPI.listEnrolledFaces(); setEnrolledFaces(r.data); }
    catch { /* ignore */ }
  }, []);

  const loadLog = useCallback(async () => {
    setLogLoading(true);
    try { const r = await transportAPI.getRecognitionLog({ type: logType || undefined }); setLog(r.data); }
    catch { toast.error('Failed to load log'); }
    finally { setLogLoading(false); }
  }, [logType]);

  useEffect(() => {
    checkHealth(); loadFaces();
    transportAPI.getEmployees().then(r => setEmployees(r.data || [])).catch(() => {});
  }, [checkHealth, loadFaces]);
  useEffect(() => { if (subTab === 'log') loadLog(); }, [subTab, loadLog]);

  const doRecognize = async () => {
    if (!recImage) return toast.error('Upload an image first');
    setRecLoading(true); setRecResult(null);
    try {
      const fn = recType === 'plate' ? transportAPI.recognizePlate : transportAPI.recognizeFace;
      const r  = await fn({ image_base64: recImage });
      setRecResult({ type: recType, ...r.data });
    } catch (e) { toast.error(e.response?.data?.error || 'Recognition failed'); }
    finally { setRecLoading(false); }
  };

  const doEnroll = async () => {
    if (!enrollImage)  return toast.error('Upload a photo');
    if (!enrollId.trim())   return toast.error((enrollCat === 'driver' || enrollCat === 'helper') ? 'Select an employee' : 'Enter student ID');
    if (!enrollName.trim()) return toast.error('Name is required');
    setEnrollLoading(true);
    try {
      await transportAPI.enrollFace({ image_base64: enrollImage, person_id: enrollId, category: enrollCat, name: enrollName });
      toast.success(`Face enrolled for ${enrollName}`);
      setEnrollImage(null); setEnrollName(''); setEnrollId('');
      loadFaces();
    } catch (e) { toast.error(e.response?.data?.error || 'Enrollment failed'); }
    finally { setEnrollLoading(false); }
  };

  const revokeEnroll = async id => {
    if (!window.confirm('Remove this face enrollment?')) return;
    try { await transportAPI.revokeEnrolledFace(id); toast.success('Removed'); loadFaces(); }
    catch { toast.error('Failed'); }
  };

  const confColor = c => c >= 0.85 ? 'text-green-600' : c >= 0.65 ? 'text-amber-600' : 'text-red-500';

  return (
    <div>
      {/* Service status banner */}
      <div className={`flex items-center justify-between p-3 rounded-xl mb-5 text-sm
        ${serviceUp === null ? 'bg-slate-50 text-slate-500' :
          serviceUp.online ? 'bg-green-50 text-green-800 border border-green-200' :
          'bg-red-50 text-red-800 border border-red-200'}`}>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${serviceUp?.online ? 'bg-green-500' : 'bg-red-400'}`} />
          {serviceUp === null ? 'Checking recognition service…' :
            serviceUp.online
              ? `Recognition service online · ${serviceUp.enrolled_faces ?? 0} faces enrolled`
              : 'Recognition service offline — start it with PM2 (see setup guide below)'}
        </div>
        <button onClick={checkHealth} className="text-xs underline opacity-70 hover:opacity-100">Refresh</button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 mb-5 w-fit">
        {[['recognize','Test Recognition'],['enroll','Enroll Faces'],['log','Recognition Log']].map(([id, label]) => (
          <button key={id} onClick={()=>setSubTab(id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all
              ${subTab===id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Test Recognition ───────────────────────────────────── */}
      {subTab === 'recognize' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex gap-2">
              {[['plate','🚗 License Plate'],['face','👤 Face']].map(([t,l])=>(
                <button key={t} onClick={()=>{setRecType(t);setRecImage(null);setRecResult(null);}}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-all
                    ${recType===t ? 'bg-blue-600 text-white border-blue-600' : 'text-slate-600 border-slate-300 hover:border-blue-400'}`}>
                  {l}
                </button>
              ))}
            </div>

            <ImageUploadBox
              label={recType==='plate' ? 'Upload photo of license plate' : 'Upload photo of person'}
              onImage={img=>{setRecImage(img);setRecResult(null);}}
              preview={recImage}
              disabled={recLoading}
            />

            <button onClick={doRecognize} disabled={recLoading || !recImage || !serviceUp?.online}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              <Eye size={15} /> {recLoading ? 'Analysing…' : `Recognize ${recType === 'plate' ? 'Plate' : 'Face'}`}
            </button>
          </div>

          <div>
            {recResult ? (
              <div className={`rounded-2xl border p-5 h-full
                ${recResult.success ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50'}`}>
                <div className="flex items-center gap-2 mb-4">
                  <div className={`w-3 h-3 rounded-full ${recResult.success ? 'bg-green-500' : 'bg-slate-400'}`} />
                  <span className="font-semibold text-slate-800">{recResult.success ? 'Match Found' : 'No Match'}</span>
                </div>

                {recResult.type === 'plate' && recResult.success && (
                  <div className="space-y-3">
                    <div className="bg-white rounded-xl p-4 text-center">
                      <p className="text-3xl font-mono font-bold text-blue-700 tracking-widest">{recResult.plate_text}</p>
                      <p className={`text-sm mt-1 font-medium ${confColor(recResult.confidence)}`}>
                        {(recResult.confidence * 100).toFixed(1)}% confidence
                      </p>
                    </div>
                    {recResult.vehicle && (
                      <div className="bg-blue-100 rounded-xl p-3 text-sm">
                        <p className="font-medium text-blue-800">Vehicle matched in database</p>
                        <p className="text-blue-600">{recResult.vehicle.vehicle_number}</p>
                        <p className="text-xs text-blue-500 mt-0.5">ANPR log entry created automatically</p>
                      </div>
                    )}
                    {recResult.all_detections?.length > 1 && (
                      <div className="text-xs text-slate-500">
                        {recResult.all_detections.length} text regions detected · showing best match
                      </div>
                    )}
                  </div>
                )}

                {recResult.type === 'plate' && !recResult.success && (
                  <p className="text-slate-500 text-sm">No readable license plate detected. Try a clearer photo with better lighting.</p>
                )}

                {recResult.type === 'face' && recResult.success && recResult.best_match && (
                  <div className="space-y-3">
                    <div className="bg-white rounded-xl p-4">
                      <p className="text-lg font-bold text-slate-800">{recResult.best_match.name}</p>
                      <p className="text-sm text-slate-500 capitalize">{recResult.best_match.category} · ID: {recResult.best_match.person_id}</p>
                      <p className={`text-sm font-medium mt-2 ${confColor(recResult.best_match.confidence)}`}>
                        {(recResult.best_match.confidence * 100).toFixed(1)}% confidence
                      </p>
                    </div>
                    <p className="text-xs text-slate-400">Checked against {recResult.faces_checked} enrolled face(s)</p>
                  </div>
                )}

                {recResult.type === 'face' && !recResult.success && (
                  <div className="text-sm text-slate-500">
                    <p>{recResult.message || 'Person not recognized.'}</p>
                    {recResult.faces_checked > 0 && <p className="mt-1 text-xs text-slate-400">Checked against {recResult.faces_checked} enrolled face(s)</p>}
                  </div>
                )}

                {recResult.action && (
                  <div className="mt-3 text-xs text-slate-500 bg-slate-100 rounded-lg px-3 py-2">
                    Action: {recResult.action}
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 h-full flex items-center justify-center min-h-48">
                <div className="text-center text-slate-400">
                  <Camera size={36} className="mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Result will appear here</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Enroll Faces ───────────────────────────────────────── */}
      {subTab === 'enroll' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-700">Enroll New Face</h3>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Category</label>
              <div className="flex gap-2">
                {['driver','helper','student'].map(c=>(
                  <button key={c} onClick={()=>setEnrollCat(c)}
                    className={`flex-1 py-2 rounded-xl text-sm font-medium border capitalize transition-all
                      ${enrollCat===c ? 'bg-blue-600 text-white border-blue-600' : 'text-slate-600 border-slate-300 hover:border-blue-400'}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            {(enrollCat === 'driver' || enrollCat === 'helper') ? (
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Select Employee *</label>
                <select className={inputCls} value={enrollId}
                  onChange={e => {
                    const emp = employees.find(x => String(x.id) === e.target.value);
                    setEnrollId(e.target.value);
                    setEnrollName(emp?.name || '');
                  }}>
                  <option value="">— choose employee —</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={String(emp.id)}>
                      {emp.name}{emp.employee_code ? ` (${emp.employee_code})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Full Name *</label>
                  <input className={inputCls} placeholder="e.g. Student Name" value={enrollName} onChange={e=>setEnrollName(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Student ID *</label>
                  <input className={inputCls} placeholder="e.g. 1042" value={enrollId} onChange={e=>setEnrollId(e.target.value)} />
                </div>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Photo (front-facing, clear lighting)</label>
              <ImageUploadBox label="Upload or drag photo" onImage={setEnrollImage} preview={enrollImage} disabled={enrollLoading} />
            </div>
            <button onClick={doEnroll} disabled={enrollLoading || !enrollImage || !serviceUp?.online}
              className="w-full flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50">
              <Shield size={15} /> {enrollLoading ? 'Enrolling…' : 'Enroll Face'}
            </button>
            <p className="text-xs text-slate-400">Use a clear, well-lit front-facing photo. Sunglasses or head coverings reduce accuracy.</p>
          </div>

          <div>
            <h3 className="font-semibold text-slate-700 mb-3">Enrolled Faces ({enrolledFaces.length})</h3>
            {enrolledFaces.length === 0
              ? <p className="text-sm text-slate-400 py-8 text-center">No faces enrolled yet</p>
              : <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                  {enrolledFaces.map(f => (
                    <div key={f.id} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{f.person_name}</p>
                        <p className="text-xs text-slate-500 capitalize">{f.person_category} · ID: {f.person_id}</p>
                        <p className="text-xs text-slate-400">{f.enrolled_at ? new Date(f.enrolled_at).toLocaleDateString('en-IN') : ''}</p>
                      </div>
                      <button onClick={()=>revokeEnroll(f.id)} className="text-red-400 hover:text-red-600 ml-4" title="Remove enrollment">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
            }
          </div>
        </div>
      )}

      {/* ── Recognition Log ────────────────────────────────────── */}
      {subTab === 'log' && (
        <div>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <h3 className="font-semibold text-slate-700">Recognition Events</h3>
            <select className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" value={logType} onChange={e=>{setLogType(e.target.value);}}>
              <option value="">All types</option>
              <option value="plate">Plate only</option>
              <option value="face">Face only</option>
            </select>
            <button onClick={loadLog} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
              <RefreshCw size={13} /> Refresh
            </button>
          </div>
          {logLoading
            ? <div className="flex justify-center py-12"><RefreshCw className="animate-spin text-blue-500" size={24} /></div>
            : <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600 uppercase">
                    <tr>{['Time','Type','Result','Confidence','Matched','Action'].map(h=><th key={h} className="px-3 py-2.5 text-left">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {log.map(l => (
                      <tr key={l.id} className="hover:bg-slate-50">
                        <td className="px-3 py-2 text-slate-500">{l.recognized_at ? new Date(l.recognized_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded-full font-medium ${l.recognition_type==='plate' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                            {l.recognition_type}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-medium">{l.result_text || '—'}</td>
                        <td className="px-3 py-2">
                          {l.confidence != null
                            ? <span className={confColor(l.confidence)}>{(l.confidence*100).toFixed(0)}%</span>
                            : '—'}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${l.matched ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                            {l.matched ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-500">{l.action_taken || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!log.length && <p className="text-center py-10 text-slate-400">No recognition events yet</p>}
              </div>
          }
        </div>
      )}

      {/* Setup guide (collapsed by default) */}
      {!serviceUp?.online && (
        <details className="mt-6">
          <summary className="text-sm text-blue-700 cursor-pointer hover:underline font-medium">Setup Guide — How to start the recognition service</summary>
          <div className="mt-3 bg-slate-900 text-green-400 rounded-xl p-4 text-xs font-mono space-y-1 leading-relaxed">
            <p className="text-slate-400"># On the Linux server — one-time setup</p>
            <p>sudo apt-get install -y cmake build-essential python3-pip</p>
            <p>cd /path/to/kulish-dashboard/backend/python-services/anpr_face</p>
            <p>pip3 install -r requirements.txt</p>
            <p className="text-slate-400 mt-2"># Start with PM2</p>
            <p>pm2 start "uvicorn main:app --host 0.0.0.0 --port 5001" --name kulish-recognition</p>
            <p>pm2 save</p>
            <p className="text-slate-400 mt-2"># Add to backend/.env</p>
            <p>RECOGNITION_SERVICE_URL=http://localhost:5001</p>
          </div>
        </details>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// FEES TAB
// ═══════════════════════════════════════════════════════════════════
const BLANK_FEE = { name: '', route_id: '', fee_period: 'monthly', amount: '', effective_from: '', effective_to: '', description: '' };
const BLANK_PAY = { amount_paid: '', payment_date: today(), payment_mode: 'cash', reference_no: '', period_label: '', remarks: '' };
const MONTHS = ['April','May','June','July','August','September','October','November','December','January','February','March'];
const fmtINR = n => `₹${parseFloat(n||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function FeesTab() {
  const [subTab,        setSubTab]        = useState('dues');
  const [routes,        setRoutes]        = useState([]);
  const [structures,    setStructures]    = useState([]);
  const [studentFees,   setStudentFees]   = useState([]);
  const [payments,      setPayments]      = useState([]);
  const [report,        setReport]        = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [filterRoute,   setFilterRoute]   = useState('');
  const [filterAY,      setFilterAY]      = useState('2026-27');

  // Fee structure modal
  const [feeModal,      setFeeModal]      = useState(false);
  const [feeForm,       setFeeForm]       = useState(BLANK_FEE);
  const [feeSaving,     setFeeSaving]     = useState(false);

  // Assign modal
  const [assignModal,   setAssignModal]   = useState(null); // student row
  const [assignStructId,setAssignStructId]= useState('');
  const [assignDiscount,setAssignDiscount]= useState('0');
  const [assignReason,  setAssignReason]  = useState('');
  const [assignSaving,  setAssignSaving]  = useState(false);

  // Bulk assign
  const [bulkModal,     setBulkModal]     = useState(false);
  const [bulkRouteId,   setBulkRouteId]   = useState('');
  const [bulkStructId,  setBulkStructId]  = useState('');
  const [bulkSaving,    setBulkSaving]    = useState(false);

  // Collect payment modal
  const [payModal,      setPayModal]      = useState(null); // student fee row
  const [payForm,       setPayForm]       = useState(BLANK_PAY);
  const [paySaving,     setPaySaving]     = useState(false);

  // Receipt modal
  const [receiptData,   setReceiptData]   = useState(null);

  const loadRoutes      = useCallback(() => transportAPI.getRoutes().then(r=>setRoutes(r.data)).catch(()=>{}), []);
  const loadStructures  = useCallback(async () => {
    const r = await transportAPI.getFeeStructures(); setStructures(r.data);
  }, []);
  const loadStudentFees = useCallback(async () => {
    setLoading(true);
    try {
      const r = await transportAPI.getStudentFees({ academic_year: filterAY, route_id: filterRoute || undefined });
      setStudentFees(r.data);
    } catch { toast.error('Failed to load fees'); }
    finally { setLoading(false); }
  }, [filterAY, filterRoute]);
  const loadPayments = useCallback(async () => {
    setLoading(true);
    try { const r = await transportAPI.getPaymentHistory(); setPayments(r.data); }
    catch { toast.error('Failed to load payments'); }
    finally { setLoading(false); }
  }, []);
  const loadReport = useCallback(async () => {
    setLoading(true);
    try { const r = await transportAPI.getFeeReport({ academic_year: filterAY }); setReport(r.data); }
    catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  }, [filterAY]);

  useEffect(() => { loadRoutes(); loadStructures(); }, [loadRoutes, loadStructures]);
  useEffect(() => {
    if (subTab === 'dues' || subTab === 'assign') loadStudentFees();
    if (subTab === 'payments') loadPayments();
    if (subTab === 'report') loadReport();
  }, [subTab, loadStudentFees, loadPayments, loadReport]);

  const saveFeeStructure = async () => {
    if (!feeForm.name || !feeForm.amount || !feeForm.effective_from) return toast.error('Name, amount, date required');
    setFeeSaving(true);
    try {
      if (feeForm.id) await transportAPI.updateFeeStructure(feeForm.id, feeForm);
      else            await transportAPI.createFeeStructure(feeForm);
      toast.success('Fee structure saved');
      setFeeModal(false); loadStructures();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
    finally { setFeeSaving(false); }
  };

  const deleteFeeStructure = async id => {
    if (!window.confirm('Delete this fee structure?')) return;
    try { await transportAPI.deleteFeeStructure(id); toast.success('Deleted'); loadStructures(); }
    catch { toast.error('Failed'); }
  };

  const doAssign = async () => {
    if (!assignStructId) return toast.error('Select a fee structure');
    setAssignSaving(true);
    try {
      await transportAPI.assignFee({ student_id: assignModal.student_id, fee_structure_id: assignStructId, academic_year: filterAY, discount_pct: assignDiscount, discount_reason: assignReason });
      toast.success('Fee assigned'); setAssignModal(null); loadStudentFees();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    finally { setAssignSaving(false); }
  };

  const doBulkAssign = async () => {
    if (!bulkRouteId || !bulkStructId) return toast.error('Select route and fee structure');
    setBulkSaving(true);
    try {
      const r = await transportAPI.bulkAssignFees({ route_id: bulkRouteId, fee_structure_id: bulkStructId, academic_year: filterAY });
      toast.success(r.data.message); setBulkModal(false); loadStudentFees();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    finally { setBulkSaving(false); }
  };

  const openPayModal = row => { setPayModal(row); setPayForm({ ...BLANK_PAY, amount_paid: row.outstanding > 0 ? row.outstanding : '' }); };

  const doPayment = async () => {
    if (!payForm.amount_paid || !payForm.payment_date) return toast.error('Amount and date required');
    setPaySaving(true);
    try {
      const r = await transportAPI.recordPayment({ ...payForm, student_id: payModal.student_id, fee_assignment_id: payModal.id });
      toast.success(`Payment recorded · Receipt ${r.data.receipt_no}`);
      setReceiptData(r.data.payment);
      setPayModal(null);
      loadStudentFees();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    finally { setPaySaving(false); }
  };

  const cancelPay = async id => {
    if (!window.confirm('Cancel this payment?')) return;
    try { await transportAPI.cancelPayment(id); toast.success('Cancelled'); loadPayments(); }
    catch { toast.error('Failed'); }
  };

  const outstandingRows = studentFees.filter(r => parseFloat(r.outstanding) > 0);

  const CHART_COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4'];

  return (
    <div>
      {/* Top controls */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex gap-2 items-center">
          <select className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" value={filterAY} onChange={e=>setFilterAY(e.target.value)}>
            {['2025-26','2026-27','2027-28'].map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          <select className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" value={filterRoute} onChange={e=>setFilterRoute(e.target.value)}>
            <option value="">All Routes</option>
            {routes.map(r=><option key={r.id} value={r.id}>{r.route_code} – {r.route_name}</option>)}
          </select>
        </div>
        <div className="flex gap-2">
          <button onClick={()=>setBulkModal(true)} className="flex items-center gap-2 text-sm border border-slate-300 px-3 py-1.5 rounded-lg text-slate-700 hover:bg-slate-50">
            <Users size={14} /> Bulk Assign
          </button>
          <button onClick={()=>{setFeeForm(BLANK_FEE);setFeeModal(true);}} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
            <Plus size={14} /> New Fee Structure
          </button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 mb-5 w-fit">
        {[['dues','Outstanding Dues'],['assign','All Students'],['payments','Payment History'],['structures','Fee Structures'],['report','Summary Report']].map(([id,label])=>(
          <button key={id} onClick={()=>setSubTab(id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all
              ${subTab===id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            {id==='dues' && outstandingRows.length > 0 ? `${label} (${outstandingRows.length})` : label}
          </button>
        ))}
      </div>

      {/* ── Outstanding Dues ─────────────────────────────────── */}
      {subTab === 'dues' && (
        loading ? <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-blue-500" size={24}/></div> :
        outstandingRows.length === 0
          ? <div className="text-center py-16 text-slate-400"><CheckCircle size={40} className="mx-auto mb-3 text-green-400"/><p className="font-medium">No outstanding dues</p></div>
          : <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-red-50 text-slate-600 text-xs uppercase">
                  <tr>{['Student','Class','Route','Total Fee','Paid','Outstanding','Last Payment',''].map(h=><th key={h} className="px-3 py-3 text-left">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {outstandingRows.map(r=>(
                    <tr key={r.id} className="hover:bg-red-50">
                      <td className="px-3 py-3"><p className="font-medium">{r.student_name}</p><p className="text-xs text-slate-400">{r.father_name}</p></td>
                      <td className="px-3 py-3 text-slate-600">{r.class_name} {r.section}</td>
                      <td className="px-3 py-3 text-blue-700 font-medium">{r.route_code||'—'}</td>
                      <td className="px-3 py-3">{fmtINR(r.total_fee)}</td>
                      <td className="px-3 py-3 text-green-700">{fmtINR(r.total_paid)}</td>
                      <td className="px-3 py-3 font-bold text-red-600">{fmtINR(r.outstanding)}</td>
                      <td className="px-3 py-3 text-slate-400 text-xs">{r.last_payment_date ? new Date(r.last_payment_date).toLocaleDateString('en-IN') : 'Never'}</td>
                      <td className="px-3 py-3">
                        <button onClick={()=>openPayModal(r)} className="flex items-center gap-1 text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700">
                          <CreditCard size={12}/> Collect
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
      )}

      {/* ── All Students (Assignments) ───────────────────────── */}
      {subTab === 'assign' && (
        loading ? <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-blue-500" size={24}/></div> :
        studentFees.length === 0
          ? <p className="text-center py-12 text-slate-400">No fee assignments for {filterAY}. Use "Bulk Assign" to get started.</p>
          : <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase">
                  <tr>{['Student','Class','Route','Fee Structure','Per Period','Periods','Total','Paid','Outstanding','Status',''].map(h=><th key={h} className="px-3 py-2.5 text-left">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {studentFees.map(r=>(
                    <tr key={r.id} className={`hover:bg-slate-50 ${parseFloat(r.outstanding)>0 ? 'bg-red-50/30' : ''}`}>
                      <td className="px-3 py-2 font-medium">{r.student_name}</td>
                      <td className="px-3 py-2 text-slate-600">{r.class_name} {r.section}</td>
                      <td className="px-3 py-2 text-blue-700">{r.route_code||'—'}</td>
                      <td className="px-3 py-2">{r.fee_structure_name}<p className="text-slate-400 capitalize">{r.fee_period}</p></td>
                      <td className="px-3 py-2">{fmtINR(r.final_amount)}{r.discount_pct>0&&<span className="text-green-600 ml-1">(-{r.discount_pct}%)</span>}</td>
                      <td className="px-3 py-2 text-center">{r.total_periods}</td>
                      <td className="px-3 py-2 font-medium">{fmtINR(r.total_fee)}</td>
                      <td className="px-3 py-2 text-green-700">{fmtINR(r.total_paid)}</td>
                      <td className={`px-3 py-2 font-bold ${parseFloat(r.outstanding)>0?'text-red-600':'text-green-600'}`}>{fmtINR(r.outstanding)}</td>
                      <td className="px-3 py-2"><Badge label={parseFloat(r.outstanding)>0?'pending':'paid'}/></td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1.5">
                          {parseFloat(r.outstanding)>0&&<button onClick={()=>openPayModal(r)} className="text-xs bg-green-600 text-white px-2 py-1 rounded-lg"><CreditCard size={10}/></button>}
                          <button onClick={()=>{setAssignModal(r);setAssignStructId(r.fee_structure_id||'');setAssignDiscount(r.discount_pct||'0');setAssignReason(r.discount_reason||'');}} className="text-xs text-blue-600 hover:text-blue-800"><Edit2 size={12}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
      )}

      {/* ── Payment History ──────────────────────────────────── */}
      {subTab === 'payments' && (
        loading ? <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-blue-500" size={24}/></div> :
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600 uppercase">
              <tr>{['Receipt No','Date','Student','Class','Route','Period','Amount','Mode','Ref No','Collected By',''].map(h=><th key={h} className="px-3 py-2.5 text-left">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payments.map(p=>(
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-blue-700">{p.receipt_no}</td>
                  <td className="px-3 py-2">{p.payment_date ? new Date(p.payment_date+'T00:00').toLocaleDateString('en-IN') : '—'}</td>
                  <td className="px-3 py-2 font-medium">{p.student_name}</td>
                  <td className="px-3 py-2">{p.class_name} {p.section}</td>
                  <td className="px-3 py-2 text-blue-700">{p.route_code||'—'}</td>
                  <td className="px-3 py-2">{p.period_label||'—'}</td>
                  <td className="px-3 py-2 font-semibold text-green-700">{fmtINR(p.amount_paid)}</td>
                  <td className="px-3 py-2 capitalize">{p.payment_mode}</td>
                  <td className="px-3 py-2 font-mono text-slate-500">{p.reference_no||'—'}</td>
                  <td className="px-3 py-2">{p.collected_by_name||'—'}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1.5">
                      <button onClick={()=>setReceiptData(p)} className="text-slate-400 hover:text-blue-600" title="Print receipt"><Printer size={12}/></button>
                      <button onClick={()=>cancelPay(p.id)} className="text-slate-300 hover:text-red-500" title="Cancel"><X size={12}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!payments.length && <p className="text-center py-10 text-slate-400">No payments recorded yet</p>}
        </div>
      )}

      {/* ── Fee Structures ───────────────────────────────────── */}
      {subTab === 'structures' && (
        <div>
          {structures.length === 0
            ? <p className="text-center py-12 text-slate-400">No fee structures yet. Click "+ New Fee Structure" above.</p>
            : <div className="grid gap-3">
                {structures.map(s=>(
                  <div key={s.id} className="flex items-center justify-between bg-white rounded-xl border border-slate-200 px-5 py-4">
                    <div>
                      <p className="font-semibold text-slate-800">{s.name}</p>
                      <p className="text-sm text-slate-500 mt-0.5">
                        {fmtINR(s.amount)} / <span className="capitalize">{s.fee_period}</span>
                        {s.route_name && <> · Route: <span className="text-blue-700">{s.route_code}</span></>}
                        {' · '}{s.assignments} student{s.assignments !== 1 ? 's' : ''}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        From {s.effective_from}{s.effective_to ? ` to ${s.effective_to}` : ' (ongoing)'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={()=>{setFeeForm({...s});setFeeModal(true);}} className="text-blue-600 hover:text-blue-800"><Edit2 size={15}/></button>
                      <button onClick={()=>deleteFeeStructure(s.id)} className="text-red-400 hover:text-red-600"><Trash2 size={15}/></button>
                    </div>
                  </div>
                ))}
              </div>
          }
        </div>
      )}

      {/* ── Summary Report ───────────────────────────────────── */}
      {subTab === 'report' && (
        loading ? <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-blue-500" size={24}/></div> :
        report && (
          <div className="space-y-6">
            {/* KPI row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <KpiCard label="Total Billed"     value={fmtINR(report.summary?.total_billed)}     icon={IndianRupee} color="blue" small />
              <KpiCard label="Collected"        value={fmtINR(report.summary?.total_collected)}  icon={CheckCircle} color="green" small />
              <KpiCard label="Outstanding"      value={fmtINR(report.summary?.outstanding)}      icon={AlertTriangle} color={report.summary?.outstanding > 0 ? 'red' : 'slate'} small />
              <KpiCard label="Students"         value={report.summary?.total_students ?? '—'}   icon={Users} color="purple" small />
            </div>

            {/* Monthly trend */}
            {report.monthly?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Monthly Collection ({filterAY})</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={report.monthly} margin={{top:4,right:10,left:10,bottom:4}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month_label" tick={{fontSize:11}} />
                    <YAxis tick={{fontSize:11}} tickFormatter={v=>`₹${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={v=>[fmtINR(v),'Collected']} />
                    <Bar dataKey="collected" fill="#3b82f6" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Route-wise table */}
              {report.by_route?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Route-wise Collection</h3>
                  <div className="rounded-xl border border-slate-200 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-600 uppercase">
                        <tr>{['Route','Students','Billed','Collected','Outstanding'].map(h=><th key={h} className="px-3 py-2 text-left">{h}</th>)}</tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {report.by_route.map(r=>(
                          <tr key={r.route_code} className="hover:bg-slate-50">
                            <td className="px-3 py-2 font-semibold text-blue-700">{r.route_code}</td>
                            <td className="px-3 py-2 text-center">{r.students}</td>
                            <td className="px-3 py-2">{fmtINR(r.billed)}</td>
                            <td className="px-3 py-2 text-green-700">{fmtINR(r.collected)}</td>
                            <td className={`px-3 py-2 font-semibold ${(r.billed-r.collected)>0?'text-red-600':'text-green-600'}`}>{fmtINR(r.billed-r.collected)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Payment mode pie */}
              {report.by_mode?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Payment Mode Breakdown</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={report.by_mode} dataKey="total" nameKey="payment_mode" cx="50%" cy="50%" outerRadius={75} label={({payment_mode,percent})=>`${payment_mode} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                        {report.by_mode.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={v=>[fmtINR(v),'Amount']} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1 mt-2">
                    {report.by_mode.map((m,i)=>(
                      <div key={m.payment_mode} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full inline-block" style={{background:CHART_COLORS[i%CHART_COLORS.length]}}/>
                          <span className="capitalize">{m.payment_mode}</span>
                        </div>
                        <span className="font-medium">{fmtINR(m.total)} ({m.count} txns)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      )}

      {/* ── Fee Structure Modal ──────────────────────────────── */}
      {feeModal && (
        <Modal title={feeForm.id ? 'Edit Fee Structure' : 'New Fee Structure'} onClose={()=>setFeeModal(false)}>
          <div className="space-y-4">
            <Field label="Structure Name" required>
              <input className={inputCls} placeholder="e.g. Standard Monthly Fee 2026-27" value={feeForm.name} onChange={e=>setFeeForm(f=>({...f,name:e.target.value}))} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Fee Period" required>
                <select className={selectCls} value={feeForm.fee_period} onChange={e=>setFeeForm(f=>({...f,fee_period:e.target.value}))}>
                  {['monthly','quarterly','annual','per_trip'].map(p=><option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Amount (₹)" required>
                <input className={inputCls} type="number" placeholder="0.00" value={feeForm.amount} onChange={e=>setFeeForm(f=>({...f,amount:e.target.value}))} />
              </Field>
            </div>
            <Field label="Applicable Route (leave blank for all routes)">
              <select className={selectCls} value={feeForm.route_id||''} onChange={e=>setFeeForm(f=>({...f,route_id:e.target.value}))}>
                <option value="">— All Routes —</option>
                {routes.map(r=><option key={r.id} value={r.id}>{r.route_code} – {r.route_name}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Effective From" required>
                <input className={inputCls} type="date" value={feeForm.effective_from} onChange={e=>setFeeForm(f=>({...f,effective_from:e.target.value}))} />
              </Field>
              <Field label="Effective To">
                <input className={inputCls} type="date" value={feeForm.effective_to||''} onChange={e=>setFeeForm(f=>({...f,effective_to:e.target.value}))} />
              </Field>
            </div>
            <Field label="Description">
              <textarea className={inputCls} rows={2} placeholder="Optional notes…" value={feeForm.description||''} onChange={e=>setFeeForm(f=>({...f,description:e.target.value}))} />
            </Field>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={()=>setFeeModal(false)} className="px-4 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={saveFeeStructure} disabled={feeSaving} className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
                {feeSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Assign Fee Modal ─────────────────────────────────── */}
      {assignModal && (
        <Modal title={`Assign Fee — ${assignModal.student_name}`} onClose={()=>setAssignModal(null)}>
          <div className="space-y-4">
            <Field label="Fee Structure" required>
              <select className={selectCls} value={assignStructId} onChange={e=>setAssignStructId(e.target.value)}>
                <option value="">— Select —</option>
                {structures.map(s=><option key={s.id} value={s.id}>{s.name} ({fmtINR(s.amount)}/{s.fee_period})</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Discount %">
                <input className={inputCls} type="number" min="0" max="100" value={assignDiscount} onChange={e=>setAssignDiscount(e.target.value)} />
              </Field>
              <Field label="Discount Reason">
                <input className={inputCls} placeholder="e.g. Sibling discount" value={assignReason} onChange={e=>setAssignReason(e.target.value)} />
              </Field>
            </div>
            {assignStructId && (
              <div className="bg-blue-50 rounded-xl p-3 text-sm">
                {(() => {
                  const s = structures.find(x=>String(x.id)===String(assignStructId));
                  if (!s) return null;
                  const disc = parseFloat(assignDiscount)||0;
                  const amt  = parseFloat(s.amount)*(1-disc/100);
                  const periods = {annual:1,quarterly:4,per_trip:1,monthly:12}[s.fee_period]||12;
                  return <><p className="text-blue-800 font-medium">Per period: {fmtINR(amt)}</p><p className="text-blue-600 text-xs">Total for {filterAY}: {fmtINR(amt * periods)} ({periods} periods)</p></>;
                })()}
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={()=>setAssignModal(null)} className="px-4 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={doAssign} disabled={assignSaving} className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
                {assignSaving ? 'Assigning…' : 'Assign'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Bulk Assign Modal ────────────────────────────────── */}
      {bulkModal && (
        <Modal title="Bulk Assign Fees by Route" onClose={()=>setBulkModal(false)}>
          <div className="space-y-4">
            <Field label="Route" required>
              <select className={selectCls} value={bulkRouteId} onChange={e=>setBulkRouteId(e.target.value)}>
                <option value="">— Select route —</option>
                {routes.map(r=><option key={r.id} value={r.id}>{r.route_code} – {r.route_name}</option>)}
              </select>
            </Field>
            <Field label="Fee Structure" required>
              <select className={selectCls} value={bulkStructId} onChange={e=>setBulkStructId(e.target.value)}>
                <option value="">— Select structure —</option>
                {structures.map(s=><option key={s.id} value={s.id}>{s.name} ({fmtINR(s.amount)}/{s.fee_period})</option>)}
              </select>
            </Field>
            <p className="text-xs text-slate-500 bg-amber-50 rounded-lg px-3 py-2">This will assign the fee to all active students on the selected route for {filterAY}. Existing assignments are updated, not duplicated.</p>
            <div className="flex justify-end gap-3">
              <button onClick={()=>setBulkModal(false)} className="px-4 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={doBulkAssign} disabled={bulkSaving} className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
                {bulkSaving ? 'Assigning…' : 'Assign to All'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Collect Payment Modal ────────────────────────────── */}
      {payModal && (
        <Modal title={`Collect Payment — ${payModal.student_name}`} onClose={()=>setPayModal(null)}>
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-xl p-3 text-sm flex justify-between">
              <span className="text-slate-600">Outstanding</span>
              <span className="font-bold text-red-600">{fmtINR(payModal.outstanding)}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Amount (₹)" required>
                <input className={inputCls} type="number" value={payForm.amount_paid} onChange={e=>setPayForm(f=>({...f,amount_paid:e.target.value}))} />
              </Field>
              <Field label="Payment Date" required>
                <input className={inputCls} type="date" value={payForm.payment_date} onChange={e=>setPayForm(f=>({...f,payment_date:e.target.value}))} />
              </Field>
            </div>
            <Field label="Payment Mode">
              <div className="flex gap-2 flex-wrap">
                {['cash','upi','neft','cheque','dd'].map(m=>(
                  <button key={m} onClick={()=>setPayForm(f=>({...f,payment_mode:m}))}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border uppercase transition-all
                      ${payForm.payment_mode===m ? 'bg-blue-600 text-white border-blue-600' : 'text-slate-600 border-slate-300 hover:border-blue-400'}`}>
                    {m}
                  </button>
                ))}
              </div>
            </Field>
            {['upi','neft','cheque','dd'].includes(payForm.payment_mode) && (
              <Field label={payForm.payment_mode === 'upi' || payForm.payment_mode === 'neft' ? 'UTR / Transaction ID' : 'Cheque / DD Number'}>
                <input className={inputCls} placeholder="Reference number" value={payForm.reference_no} onChange={e=>setPayForm(f=>({...f,reference_no:e.target.value}))} />
              </Field>
            )}
            <Field label="Period">
              <div className="flex gap-2 flex-wrap mb-2">
                {MONTHS.map(m=>(
                  <button key={m} onClick={()=>setPayForm(f=>({...f,period_label:`${m} ${m==='January'||m==='February'||m==='March'?'2027':'2026'}`}))}
                    className={`px-2.5 py-1 rounded-lg text-xs border transition-all
                      ${payForm.period_label?.startsWith(m) ? 'bg-blue-600 text-white border-blue-600' : 'text-slate-600 border-slate-200 hover:border-blue-300'}`}>
                    {m.slice(0,3)}
                  </button>
                ))}
              </div>
              <input className={inputCls} placeholder="Or type period e.g. Annual 2026-27, Q1 2026-27…" value={payForm.period_label} onChange={e=>setPayForm(f=>({...f,period_label:e.target.value}))} />
            </Field>
            <Field label="Remarks">
              <input className={inputCls} placeholder="Optional" value={payForm.remarks} onChange={e=>setPayForm(f=>({...f,remarks:e.target.value}))} />
            </Field>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={()=>setPayModal(null)} className="px-4 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={doPayment} disabled={paySaving} className="flex items-center gap-2 px-5 py-2 text-sm bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50">
                <Receipt size={14}/> {paySaving ? 'Processing…' : 'Record & Print Receipt'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Receipt Modal ────────────────────────────────────── */}
      {receiptData && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={()=>setReceiptData(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full" onClick={e=>e.stopPropagation()} id="receipt-print">
            <div className="p-6 border-b text-center">
              <p className="font-bold text-slate-800 text-lg">The Kulish School</p>
              <p className="text-xs text-slate-500 mt-0.5">Transport Fee Receipt</p>
            </div>
            <div className="p-6 space-y-3 text-sm">
              <div className="flex justify-between text-xs text-slate-500 font-mono bg-blue-50 rounded-lg px-3 py-2">
                <span>Receipt No</span><span className="font-bold text-blue-700">{receiptData.receipt_no}</span>
              </div>
              {[
                ['Student',  receiptData.student_name],
                ['Class',    `${receiptData.class_name} ${receiptData.section}`],
                ['Date',     receiptData.payment_date ? new Date(receiptData.payment_date+'T00:00').toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'}) : ''],
                ['Period',   receiptData.period_label || '—'],
                ['Mode',     (receiptData.payment_mode||'').toUpperCase()],
                ['Ref No',   receiptData.reference_no || '—'],
              ].map(([k,v])=>(
                <div key={k} className="flex justify-between border-b border-dashed border-slate-100 pb-2">
                  <span className="text-slate-500">{k}</span><span className="font-medium text-slate-800">{v}</span>
                </div>
              ))}
              <div className="flex justify-between bg-green-50 rounded-xl px-4 py-3 mt-2">
                <span className="font-semibold text-slate-700">Amount Paid</span>
                <span className="text-xl font-bold text-green-700">{fmtINR(receiptData.amount_paid)}</span>
              </div>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button onClick={()=>{window.print();}} className="flex-1 flex items-center justify-center gap-2 border border-slate-300 text-slate-700 py-2 rounded-xl text-sm hover:bg-slate-50">
                <Printer size={14}/> Print
              </button>
              <button onClick={()=>setReceiptData(null)} className="flex-1 bg-blue-600 text-white py-2 rounded-xl text-sm font-medium hover:bg-blue-700">
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════
export default function Transport() {
  const [activeTab, setActiveTab] = useState('dashboard');

  return (
    <div className="p-6 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
          <Bus className="text-white" size={20} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-800">Transport Management</h1>
          <p className="text-xs text-slate-500">Vehicles · Routes · Attendance · GPS Tracking · ANPR · Analytics</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-6 bg-slate-100 p-1.5 rounded-xl">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
              activeTab === t.id ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'
            }`}>
            <t.icon size={13} />{t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'dashboard'  && <DashboardTab />}
        {activeTab === 'vehicles'   && <VehiclesTab />}
        {activeTab === 'routes'     && <RoutesTab />}
        {activeTab === 'students'   && <StudentsTab />}
        {activeTab === 'attendance' && <AttendanceTab />}
        {activeTab === 'gps'        && <GpsTrackingTab />}
        {activeTab === 'anpr'       && <AnprGatesTab />}
        {activeTab === 'analytics'  && <AnalyticsTab />}
        {activeTab === 'exceptions'    && <ExceptionsTab />}
        {activeTab === 'reports'       && <ReportsTab />}
        {activeTab === 'notifications' && <NotificationsTab />}
        {activeTab === 'optimization'  && <RouteOptimizationTab />}
        {activeTab === 'recognition'   && <RecognitionTab />}
        {activeTab === 'fees'          && <FeesTab />}
        {activeTab === 'livecctv'      && <LiveCCTVTab />}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// LIVE CCTV TAB
// ═══════════════════════════════════════════════════════════════════
function LiveCCTVTab() {
  const [cameras,    setCameras]    = useState([]);
  const [events,     setEvents]     = useState([]);
  const [stats,      setStats]      = useState(null);
  const [subTab,     setSubTab]     = useState('feed');
  const [loading,    setLoading]    = useState(true);
  const [toggling,   setToggling]   = useState({});
  const [showForm,   setShowForm]   = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [form,       setForm]       = useState({ name:'', rtsp_url:'', gate_name:'', location:'', detection_mode:'both', capture_interval:3 });
  const intervalRef = useRef(null);

  const BLANK = { name:'', rtsp_url:'', gate_name:'', location:'', detection_mode:'both', capture_interval:3 };

  const load = async () => {
    try {
      const [camR, evR, stR] = await Promise.all([
        transportAPI.getCCTVCameras(),
        transportAPI.getCCTVEvents({ limit: 60 }),
        transportAPI.getCCTVStats(),
      ]);
      setCameras(camR.data || []);
      setEvents(evR.data || []);
      setStats(stR.data || null);
    } catch {}
    setLoading(false);
  };

  const pollEvents = async () => {
    try {
      const r = await transportAPI.getCCTVEvents({ limit: 60 });
      setEvents(r.data || []);
      const s = await transportAPI.getCCTVStats();
      setStats(s.data || null);
    } catch {}
  };

  useEffect(() => {
    load();
    intervalRef.current = setInterval(pollEvents, 3000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const toggleMonitor = async (cam) => {
    setToggling(t => ({ ...t, [cam.id]: true }));
    try {
      if (cam.is_monitoring) {
        await transportAPI.stopCCTVMonitoring(cam.id);
        toast.success(`Stopped monitoring ${cam.name}`);
      } else {
        await transportAPI.startCCTVMonitoring(cam.id);
        toast.success(`Started monitoring ${cam.name}`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed');
    }
    setToggling(t => ({ ...t, [cam.id]: false }));
  };

  const saveCamera = async () => {
    try {
      if (editing) {
        await transportAPI.updateCCTVCamera(editing.id, form);
        toast.success('Camera updated');
      } else {
        await transportAPI.createCCTVCamera(form);
        toast.success('Camera added');
      }
      setShowForm(false); setEditing(null); setForm(BLANK);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  const deleteCamera = async (cam) => {
    if (!window.confirm(`Remove camera "${cam.name}"?`)) return;
    try {
      await transportAPI.deleteCCTVCamera(cam.id);
      toast.success('Camera removed');
      load();
    } catch (e) { toast.error('Failed to remove'); }
  };

  const openEdit = (cam) => {
    setEditing(cam);
    setForm({ name: cam.name, rtsp_url: cam.rtsp_url, gate_name: cam.gate_name||'',
              location: cam.location||'', detection_mode: cam.detection_mode||'both',
              capture_interval: cam.capture_interval||3 });
    setShowForm(true);
  };

  const monitoringCount = cameras.filter(c => c.is_monitoring).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${monitoringCount > 0 ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`} />
          <span className="font-semibold text-slate-800">
            {monitoringCount > 0 ? `${monitoringCount} camera${monitoringCount > 1 ? 's' : ''} live` : 'No cameras active'}
          </span>
          {stats && (
            <span className="text-xs text-slate-500">
              · {stats.today?.total_events || 0} detections today · {stats.today?.vehicles_marked || 0} vehicles · {stats.today?.staff_marked || 0} staff marked
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
            {[{id:'feed',label:'Live Feed'},{id:'cameras',label:'Cameras'},{id:'setup',label:'How to Setup'}].map(t=>(
              <button key={t.id} onClick={()=>setSubTab(t.id)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${subTab===t.id?'bg-white text-blue-700 shadow-sm':'text-slate-600 hover:text-slate-800'}`}>
                {t.label}
              </button>
            ))}
          </div>
          {subTab==='cameras' && (
            <button onClick={()=>{ setEditing(null); setForm(BLANK); setShowForm(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium">
              + Add Camera
            </button>
          )}
        </div>
      </div>

      {/* ── Live Feed ────────────────────────────────────────────── */}
      {subTab === 'feed' && (
        <div className="space-y-3">
          {/* Stats row */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { label: 'Detections Today',  value: stats.today?.total_events    || 0, color: 'text-blue-600' },
                { label: 'Vehicles Marked',   value: stats.today?.vehicles_marked  || 0, color: 'text-green-600' },
                { label: 'Staff Marked',      value: stats.today?.staff_marked     || 0, color: 'text-emerald-600' },
                { label: 'Unique Vehicles',   value: stats.today?.unique_vehicles  || 0, color: 'text-violet-600' },
                { label: 'Active Cameras',    value: `${stats.cameras?.monitoring||0}/${stats.cameras?.total||0}`, color: monitoringCount>0?'text-green-600':'text-slate-500' },
              ].map(s => (
                <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-3 text-center">
                  <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-xs text-slate-500">{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Events stream */}
          {events.length === 0
            ? <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center">
                <Eye size={32} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">No detections yet</p>
                <p className="text-xs text-slate-400 mt-1">Configure cameras and start monitoring to see live events here</p>
              </div>
            : <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
                {events.map(ev => (
                  <div key={ev.id} className="bg-white rounded-xl border border-slate-200 p-3 flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${ev.vehicle_marked ? 'bg-green-100' : 'bg-slate-100'}`}>
                      <Bus size={14} className={ev.vehicle_marked ? 'text-green-600' : 'text-slate-400'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {ev.plate_text && (
                          <span className="font-bold text-sm tracking-widest text-slate-800">{ev.plate_text}</span>
                        )}
                        {ev.vehicle_number && ev.vehicle_number !== ev.plate_text && (
                          <span className="text-xs text-slate-500">→ {ev.vehicle_number}</span>
                        )}
                        {ev.person_name && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full capitalize">
                            {ev.person_category}: {ev.person_name}
                          </span>
                        )}
                        <span className="ml-auto text-xs text-slate-400 shrink-0">
                          {new Date(ev.event_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {ev.camera_name && <span className="text-xs text-slate-400">{ev.camera_name}{ev.gate_name ? ` · ${ev.gate_name}` : ''}</span>}
                        {ev.vehicle_marked ? <span className="text-xs text-green-600 flex items-center gap-0.5"><CheckCircle size={10}/>Vehicle</span> : null}
                        {ev.driver_marked  ? <span className="text-xs text-blue-600  flex items-center gap-0.5"><CheckCircle size={10}/>Driver</span>  : null}
                        {ev.helper_marked  ? <span className="text-xs text-violet-600 flex items-center gap-0.5"><CheckCircle size={10}/>Helper</span> : null}
                        {ev.plate_confidence && <span className="text-xs text-slate-400">Plate: {Math.round(ev.plate_confidence*100)}%</span>}
                        {ev.face_confidence  && <span className="text-xs text-slate-400">Face: {Math.round(ev.face_confidence*100)}%</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
          }
        </div>
      )}

      {/* ── Camera Management ─────────────────────────────────────── */}
      {subTab === 'cameras' && (
        <div className="space-y-3">
          {loading
            ? <div className="flex justify-center py-12"><RefreshCw className="animate-spin text-blue-500" size={22}/></div>
            : cameras.length === 0
              ? <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center">
                  <Camera size={28} className="text-slate-300 mx-auto mb-2"/>
                  <p className="text-slate-500">No cameras configured yet</p>
                  <button onClick={()=>{ setEditing(null); setForm(BLANK); setShowForm(true); }}
                    className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">+ Add First Camera</button>
                </div>
              : cameras.map(cam => (
                  <div key={cam.id} className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${cam.is_monitoring ? 'bg-green-100' : 'bg-slate-100'}`}>
                        <Camera size={18} className={cam.is_monitoring ? 'text-green-600' : 'text-slate-400'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-800">{cam.name}</span>
                          {cam.gate_name && <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{cam.gate_name}</span>}
                          <span className={`text-xs px-2 py-0.5 rounded-full ${cam.is_monitoring ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                            {cam.is_monitoring ? '● Live' : '○ Idle'}
                          </span>
                          <span className="text-xs text-slate-400 capitalize">{cam.detection_mode} · {cam.capture_interval}s interval</span>
                          {cam.events_today > 0 && <span className="text-xs text-blue-600">{cam.events_today} events today</span>}
                        </div>
                        <p className="text-xs text-slate-400 truncate mt-0.5">{cam.rtsp_url}</p>
                        {cam.last_event_at && <p className="text-xs text-slate-400 mt-0.5">Last detection: {new Date(cam.last_event_at).toLocaleString('en-IN')}</p>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => openEdit(cam)}
                          className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500">
                          <Edit2 size={13}/>
                        </button>
                        <button onClick={() => deleteCamera(cam)}
                          className="p-1.5 rounded-lg border border-red-100 hover:bg-red-50 text-red-400">
                          <Trash2 size={13}/>
                        </button>
                        <button onClick={() => toggleMonitor(cam)} disabled={!!toggling[cam.id]}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${cam.is_monitoring ? 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100' : 'bg-green-600 text-white hover:bg-green-700'} disabled:opacity-50`}>
                          {toggling[cam.id] ? <RefreshCw size={12} className="animate-spin"/> : cam.is_monitoring ? 'Stop' : 'Start'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
          }
        </div>
      )}

      {/* ── Setup Guide ───────────────────────────────────────────── */}
      {subTab === 'setup' && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="font-semibold text-blue-800 mb-1">Prerequisites</p>
            <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
              <li>IP Camera or DVR with RTSP stream output enabled</li>
              <li>Recognition service running: <code className="bg-blue-100 px-1 rounded">pm2 start kulish-recognition</code></li>
              <li>Driver/Helper faces enrolled in the <strong>Recognition</strong> tab</li>
              <li>Vehicles added with correct number plates in the <strong>Vehicles</strong> tab</li>
            </ul>
          </div>

          {[
            { n: '1', title: 'Find your RTSP URL', color: 'violet',
              steps: [
                'Open your DVR/NVR web interface or camera admin panel',
                'Go to Network → RTSP or Video Stream settings',
                'Typical format: rtsp://admin:password@192.168.1.100:554/stream1',
                'Hikvision: rtsp://admin:pass@IP:554/Streaming/Channels/101',
                'Dahua: rtsp://admin:pass@IP:554/cam/realmonitor?channel=1&subtype=0',
                'Test in VLC: Media → Open Network Stream → paste RTSP URL',
              ]},
            { n: '2', title: 'Add camera in this tab', color: 'blue',
              steps: [
                'Go to Cameras sub-tab → click + Add Camera',
                'Enter name (e.g. "Main Gate Entry"), RTSP URL, gate name',
                'Set Detection Mode: Both (recommended) / Plate only / Face only',
                'Set Capture Interval: 3 seconds recommended for gate cameras',
                'Save the camera',
              ]},
            { n: '3', title: 'Start monitoring', color: 'green',
              steps: [
                'Click Start on the camera card',
                'Python service connects to RTSP stream in background',
                'Every 3 seconds: captures frame → runs ANPR + face recognition',
                'When plate matched → Vehicle attendance marked (route_started)',
                'When driver/helper face matched → Staff attendance marked (present)',
                'All events appear in the Live Feed tab in real-time',
              ]},
            { n: '4', title: 'Camera placement tips', color: 'amber',
              steps: [
                'Mount at gate exit/entry height (2–3 m) angled at vehicles',
                'Plate should fill at least 15% of frame width for best ANPR accuracy',
                'Good lighting is critical — add IR illuminator for night use',
                'For face recognition: separate camera at driver window height (~1.5 m)',
                'Avoid backlighting (do not place camera facing the sun)',
              ]},
          ].map(section => (
            <div key={section.n} className={`rounded-xl border border-${section.color}-200 bg-${section.color}-50 p-4`}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-7 h-7 rounded-full bg-${section.color}-600 text-white flex items-center justify-center text-xs font-bold`}>{section.n}</div>
                <p className={`font-semibold text-${section.color}-800`}>{section.title}</p>
              </div>
              <ul className={`text-sm text-${section.color}-700 space-y-1 list-disc list-inside`}>
                {section.steps.map((s,i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          ))}

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="font-semibold text-slate-700 mb-2">Common RTSP URL formats</p>
            <div className="space-y-1 font-mono text-xs text-slate-600">
              <p><span className="text-slate-400">Hikvision:  </span>rtsp://admin:Password@192.168.1.64:554/Streaming/Channels/101</p>
              <p><span className="text-slate-400">Dahua:      </span>rtsp://admin:Password@192.168.1.108:554/cam/realmonitor?channel=1&subtype=0</p>
              <p><span className="text-slate-400">CP Plus:    </span>rtsp://admin:admin123@192.168.1.10:554/stream1</p>
              <p><span className="text-slate-400">Generic:    </span>rtsp://username:password@IP_ADDRESS:554/stream</p>
            </div>
          </div>
        </div>
      )}

      {/* ── Add/Edit Camera Modal ─────────────────────────────────── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4">
            <h3 className="font-bold text-lg text-slate-800">{editing ? 'Edit Camera' : 'Add CCTV Camera'}</h3>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Camera Name *</label>
                <input value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}
                  placeholder="e.g. Main Gate Entry"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">RTSP URL *</label>
                <input value={form.rtsp_url} onChange={e=>setForm(f=>({...f,rtsp_url:e.target.value}))}
                  placeholder="rtsp://admin:password@192.168.1.100:554/stream1"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Gate Name</label>
                <input value={form.gate_name} onChange={e=>setForm(f=>({...f,gate_name:e.target.value}))}
                  placeholder="e.g. Main Gate"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Location</label>
                <input value={form.location} onChange={e=>setForm(f=>({...f,location:e.target.value}))}
                  placeholder="e.g. Entry, Exit"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Detection Mode</label>
                <select value={form.detection_mode} onChange={e=>setForm(f=>({...f,detection_mode:e.target.value}))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                  <option value="both">Both (Plate + Face)</option>
                  <option value="plate">Plate only (ANPR)</option>
                  <option value="face">Face only</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Capture Interval (seconds)</label>
                <input type="number" min="1" max="30" value={form.capture_interval}
                  onChange={e=>setForm(f=>({...f,capture_interval:parseInt(e.target.value)||3}))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-700"><strong>Tip:</strong> Test your RTSP URL in VLC first. Set interval to 3s for gate cameras, 5–10s for general area cameras.</p>
            </div>

            <div className="flex gap-2 justify-end">
              <button onClick={()=>{ setShowForm(false); setEditing(null); setForm(BLANK); }}
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600">Cancel</button>
              <button onClick={saveCamera} disabled={!form.name || !form.rtsp_url}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-40">
                {editing ? 'Update Camera' : 'Add Camera'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
